<template>
  <section class="erp-page">
    <header class="erp-hero">
      <div>
        <span class="eyebrow">SUNGDUK COMPUTER ERP</span>
        <h1>업무와 회계를 한 흐름으로</h1>
        <p>거래처, 계정과목, 전표를 관리합니다. 견적서는 기존 화면을 그대로 사용합니다.</p>
      </div>
      <div class="hero-status">
        <span :class="['status-dot', { ready: health.ok }]"></span>
        {{ health.ok ? `DB 연결 · 계정과목 ${health.accounts}개` : "연결 확인 중" }}
      </div>
    </header>

    <nav class="erp-tabs" aria-label="ERP 메뉴">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        :class="{ active: activeTab === tab.key }"
        @click="selectTab(tab.key)"
      >
        {{ tab.label }}
      </button>
      <RouterLink to="/estimate" class="estimate-link">기존 견적서 ↗</RouterLink>
    </nav>

    <p v-if="notice.text" :class="['notice', notice.type]">{{ notice.text }}</p>

    <template v-if="activeTab === 'works'">
      <div class="sub-tabs">
        <button v-for="tab in workSubTabs" :key="tab.key" type="button" :class="{ active: workSubTab === tab.key }" @click="workSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', 'work-layout', { single: workSubTab !== 'overview' }]">
        <section v-if="workSubTab === 'form' || workSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>{{ workForm.id ? `${workForm.work_no} 수정` : "새 작업 등록" }}</h2>
            </div>
            <div class="heading-actions">
              <button v-if="workForm.id" type="button" class="text-button" @click="resetWorkForm">새 작업</button>
              <button v-if="workForm.id" type="button" class="button ghost danger" :disabled="saving" @click="deleteWork">
                작업 삭제
              </button>
            </div>
          </div>
          <form class="erp-form" @submit.prevent="saveWork">
            <label class="wide">
              <span>작업명 *</span>
              <input v-model.trim="workForm.title" required maxlength="255" placeholder="예: 사무실 네트워크 구축" />
            </label>
            <label>
              <span>거래처 *</span>
              <select v-model="workForm.partner_id" required>
                <option value="">거래처 선택</option>
                <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
              </select>
            </label>
            <label>
              <span>작업일 *</span>
              <input v-model="workForm.work_date" type="date" required />
            </label>
            <label>
              <span>상태</span>
              <select v-model="workForm.status">
                <option value="planned">예정</option>
                <option value="in_progress">진행 중</option>
                <option value="completed">완료</option>
                <option value="cancelled">취소</option>
              </select>
            </label>
            <div class="estimate-picker wide">
              <span>기존 견적서 연결</span>
              <div class="estimate-search">
                <input
                  v-model.trim="estimateQuery"
                  placeholder="견적번호 또는 거래처 검색"
                  @keyup.enter.prevent="loadEstimates"
                />
                <button type="button" class="button ghost" @click="loadEstimates">검색</button>
              </div>
              <select v-model="workForm.source_estimate_record_id">
                <option value="">견적서 연결 안 함</option>
                <option v-for="estimate in estimates" :key="estimate.id" :value="estimate.id">
                  {{ estimate.no }} · {{ estimate.customer_name || estimate.receiver_name || "거래처 없음" }}
                  · {{ money(estimate.total_amount) }}원 · {{ estimate.statement_issued ? "거래명세서 발행" : "거래명세서 미발행" }}
                  · {{ estimate.erp_partner_name ? `ERP ${estimate.erp_partner_name}` : "ERP 미매핑" }}
                </option>
              </select>
              <div v-if="selectedEstimate" class="selected-estimate">
                <div>
                  <strong>{{ selectedEstimate.no }}</strong>
                  <span>{{ selectedEstimate.estimate_date }} · {{ selectedEstimate.customer_name || selectedEstimate.receiver_name || "-" }}</span>
                  <span :class="['statement-state', { issued: selectedEstimate.statement_issued }]">
                    {{ selectedEstimate.statement_issued ? `거래명세서 발행 ${selectedEstimate.statement_count || 1}건` : "거래명세서 미발행" }}
                  </span>
                  <span :class="['mapping-state', { linked: selectedEstimate.erp_partner_id }]">
                    {{ selectedEstimate.erp_partner_name ? `ERP 거래처 ${selectedEstimate.erp_partner_name}` : "ERP 거래처 미매핑 · 수신 텍스트는 자동 등록하지 않음" }}
                  </span>
                </div>
                <div class="estimate-actions">
                  <button type="button" class="text-button" @click="viewEstimateById(selectedEstimate.id)">견적서 바로보기 ↗</button>
                  <button type="button" class="button ghost" :disabled="saving || !workForm.partner_id" @click="linkSelectedEstimatePartner">
                    선택 거래처로 매핑
                  </button>
                  <button type="button" class="button ghost" :disabled="saving" @click="createWorkFromSelectedEstimate">
                    ERP 작업 생성
                  </button>
                </div>
              </div>
            </div>
            <label class="wide">
              <span>작업 메모</span>
              <textarea v-model.trim="workForm.memo" rows="4"></textarea>
            </label>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetWorkForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : workForm.id ? "작업 수정" : "작업번호 생성 및 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="workSubTab === 'list' || workSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>작업 문서 허브</h2>
            </div>
            <span class="count">{{ works.length }}개</span>
          </div>
          <div class="search-row">
            <input v-model.trim="workQuery" placeholder="작업번호, 작업명, 거래처 검색" @keyup.enter="loadWorks" />
            <button type="button" class="button ghost" @click="loadWorks">검색</button>
          </div>
          <div class="data-list work-list">
            <article v-for="work in works" :key="work.id" class="work-card" @click="editWork(work)">
              <header>
                <div>
                  <strong>{{ work.work_no }}</strong>
                  <span :class="['work-status', work.status]">{{ workStatusLabel(work.status) }}</span>
                </div>
                <time>{{ work.work_date }}</time>
              </header>
              <h3>{{ work.title }}</h3>
              <p>{{ work.partner_name }}</p>
              <div class="work-links">
                <span :class="{ linked: work.estimate_no }">견적 {{ work.estimate_no || "—" }}</span>
                <span :class="{ linked: work.estimate_statement_issued }">
                  명세 {{ work.estimate_statement_issued ? "발행" : "미발행" }}
                </span>
                <span>문서 {{ work.document_count }}</span>
                <span>전표 {{ work.journal_count }}</span>
                <span>매출 {{ work.sale_count }}</span>
                <span>입금 {{ work.payment_count }}</span>
                <span>비용 {{ work.expense_count || 0 }}</span>
                <span>자산 {{ work.asset_count || 0 }}</span>
                <span>메일 {{ work.mail_count }}</span>
              </div>
            </article>
            <p v-if="!works.length && !loading" class="empty">등록된 작업이 없습니다.</p>
          </div>
        </section>
      </div>

      <section v-if="selectedWork && workSubTab === 'documents'" class="panel document-hub">
        <div class="panel-heading document-heading">
          <div>
            <span class="section-number">03</span>
            <div>
              <h2>{{ selectedWork.work_no }} 문서함</h2>
              <p>{{ selectedWork.partner_name }} · {{ selectedWork.title }}</p>
            </div>
          </div>
          <div class="heading-actions">
            <button
              v-if="selectedWork.source_estimate_record_id"
              type="button"
              class="text-button"
              @click="viewEstimateById(selectedWork.source_estimate_record_id)"
            >
              연결 견적서 ↗
            </button>
            <button
              v-if="selectedWork.source_estimate_record_id"
              type="button"
              class="button ghost"
              :disabled="saving || !selectedWork.estimate_statement_issued"
              @click="createSaleFromSelectedWorkEstimate"
            >
              {{ selectedWork.estimate_statement_issued ? "견적에서 매출 생성" : "거래명세서 발행 후 매출 생성" }}
            </button>
            <button
              v-if="selectedWork.source_estimate_record_id"
              type="button"
              class="button primary"
              :disabled="saving || !selectedWork.estimate_statement_issued"
              @click="issueInvoiceFromSelectedWorkEstimate"
            >
              {{ selectedWork.estimate_statement_issued ? "바로 세금계산서 발행" : "거래명세서 발행 필요" }}
            </button>
            <button type="button" class="text-button" @click="closeDocumentHub">닫기</button>
          </div>
        </div>

        <div class="document-types">
          <button
            v-for="type in documentTypes"
            :key="type.value"
            type="button"
            :class="{ active: documentFilter === type.value }"
            @click="documentFilter = type.value"
          >
            <span>{{ type.icon }}</span>
            <strong>{{ type.label }}</strong>
            <small>{{ documentCount(type.value) }}개</small>
          </button>
        </div>

        <div v-if="workDeleteBlockers" :class="['delete-status', { ready: workDeleteBlockers.can_delete }]">
          <strong>{{ workDeleteBlockers.can_delete ? "작업 삭제 가능" : "작업 삭제 전 정리 필요" }}</strong>
          <span>{{ workDeleteBlockers.message }}</span>
          <button type="button" class="text-button" @click="loadWorkDeleteBlockers">상태 새로고침</button>
        </div>

        <form class="upload-box" @submit.prevent="uploadDocuments">
          <label>
            <span>문서 유형</span>
            <select v-model="uploadForm.document_type">
              <option v-for="type in documentTypes.filter((item) => item.value)" :key="type.value" :value="type.value">
                {{ type.label }}
              </option>
            </select>
          </label>
          <label>
            <span>문서 제목</span>
            <input v-model.trim="uploadForm.title" maxlength="255" placeholder="선택 입력" />
          </label>
          <label class="file-picker">
            <span>파일 선택 · 파일당 최대 50MB, 한 번에 10개</span>
            <input :key="fileInputKey" type="file" multiple @change="selectFiles" />
            <b>{{ uploadFiles.length ? `${uploadFiles.length}개 선택됨` : "파일을 선택하세요" }}</b>
          </label>
          <button type="submit" class="button primary" :disabled="uploading || !uploadFiles.length">
            {{ uploading ? `업로드 중 ${uploadProgress}` : "NAS에 업로드" }}
          </button>
        </form>

        <div class="document-list">
          <article v-for="document in filteredDocuments" :key="document.id" class="document-row">
            <div class="file-icon">{{ documentIcon(document.document_type) }}</div>
            <div class="file-info">
              <strong>{{ document.title || document.file_name }}</strong>
              <span v-if="document.title">{{ document.file_name }}</span>
              <small>
                {{ documentTypeLabel(document.document_type) }} ·
                {{ fileSize(document.file_size) }} ·
                {{ compactDate(document.created_at) }}
              </small>
            </div>
            <div class="document-actions">
              <button type="button" class="button ghost" @click="downloadDocument(document)">다운로드</button>
              <button type="button" class="icon-danger" title="삭제" @click="deleteDocument(document)">×</button>
            </div>
          </article>
          <p v-if="!filteredDocuments.length && !loadingDocuments" class="empty">이 유형에 등록된 문서가 없습니다.</p>
        </div>

        <div v-if="photoDocuments.length" class="photo-gallery">
          <div class="panel-heading compact-heading">
            <div>
              <span class="section-number">04</span>
              <h2>작업사진 갤러리</h2>
            </div>
            <span class="count">{{ photoDocuments.length }}장</span>
          </div>
          <div class="photo-grid">
            <article v-for="photo in photoDocuments" :key="photo.id" class="photo-card">
              <button type="button" @click="downloadDocument(photo)">
                <img v-if="thumbnailUrls[photo.id]" :src="thumbnailUrls[photo.id]" :alt="photo.title || photo.file_name" />
                <span v-else>사진</span>
              </button>
              <strong>{{ photo.title || photo.file_name }}</strong>
              <small>{{ fileSize(photo.file_size) }} · {{ compactDate(photo.created_at) }}</small>
            </article>
          </div>
        </div>

        <div class="mail-hub">
          <div class="mail-compose">
            <div class="panel-heading compact-heading">
              <div>
                <span class="section-number">04</span>
                <h2>메일 발송 이력</h2>
              </div>
              <span class="count">{{ mailHistories.length }}건</span>
            </div>
            <form class="erp-form" @submit.prevent="saveMailHistory(false)">
              <label>
                <span>받는 사람 *</span>
                <input v-model.trim="mailForm.recipient" required maxlength="1000" placeholder="name@example.com" />
              </label>
              <label>
                <span>참조</span>
                <input v-model.trim="mailForm.cc" maxlength="1000" placeholder="쉼표로 여러 명 입력" />
              </label>
              <label class="wide">
                <span>제목 *</span>
                <input v-model.trim="mailForm.subject" required maxlength="500" />
              </label>
              <label class="wide">
                <span>본문</span>
                <textarea v-model.trim="mailForm.body" rows="5"></textarea>
              </label>
              <div class="form-actions wide">
                <button type="button" class="button ghost" @click="resetMailForm">초기화</button>
                <button type="submit" class="button ghost" :disabled="saving">이력 저장</button>
                <button type="button" class="button primary" :disabled="saving" @click="saveMailHistory(true)">
                  발송
                </button>
              </div>
            </form>
          </div>

          <div class="mail-list">
            <article v-for="mail in mailHistories" :key="mail.id" class="mail-row">
              <div>
                <strong>{{ mail.subject }}</strong>
                <span>{{ mail.recipient }}</span>
                <small>{{ mailStatusLabel(mail.status) }} · {{ compactDate(mail.sent_at || mail.created_at) }}</small>
                <small v-if="mail.error_message" class="mail-error">{{ mail.error_message }}</small>
              </div>
              <button
                v-if="mail.status !== 'sent'"
                type="button"
                class="button ghost"
                :disabled="saving"
                @click="sendMailHistory(mail)"
              >
                재발송
              </button>
            </article>
            <p v-if="!mailHistories.length && !loadingMailHistories" class="empty">메일 이력이 없습니다.</p>
          </div>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 'partners'">
      <div class="sub-tabs">
        <button v-for="tab in partnerSubTabs" :key="tab.key" type="button" :class="{ active: partnerSubTab === tab.key }" @click="partnerSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', { single: partnerSubTab !== 'overview' }]">
        <section v-if="partnerSubTab === 'form' || partnerSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>{{ partnerForm.id ? "거래처 수정" : "거래처 등록" }}</h2>
            </div>
            <button v-if="partnerForm.id" type="button" class="text-button" @click="resetPartnerForm">
              새 거래처
            </button>
          </div>
          <form class="erp-form" @submit.prevent="savePartner">
            <label class="wide">
              <span>거래처명 *</span>
              <input v-model.trim="partnerForm.name" required maxlength="150" />
            </label>
            <label>
              <span>사업자등록번호</span>
              <input v-model.trim="partnerForm.business_number" placeholder="000-00-00000" maxlength="20" />
            </label>
            <label>
              <span>대표자</span>
              <input v-model.trim="partnerForm.ceo_name" maxlength="80" />
            </label>
            <label>
              <span>업태</span>
              <input v-model.trim="partnerForm.business_type" maxlength="100" />
            </label>
            <label>
              <span>종목</span>
              <input v-model.trim="partnerForm.business_item" maxlength="100" />
            </label>
            <label>
              <span>담당자</span>
              <input v-model.trim="partnerForm.contact_name" maxlength="80" />
            </label>
            <label>
              <span>전화번호</span>
              <input v-model.trim="partnerForm.phone" maxlength="30" />
            </label>
            <label class="wide">
              <span>이메일</span>
              <input v-model.trim="partnerForm.email" type="email" maxlength="320" />
            </label>
            <label class="wide">
              <span>주소</span>
              <input v-model.trim="partnerForm.address" maxlength="500" />
            </label>
            <label class="wide">
              <span>메모</span>
              <textarea v-model.trim="partnerForm.memo" rows="3"></textarea>
            </label>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetPartnerForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : partnerForm.id ? "수정 저장" : "거래처 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="partnerSubTab === 'list' || partnerSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>거래처 목록</h2>
            </div>
            <span class="count">{{ partners.length }}개</span>
          </div>
          <div class="search-row">
            <input v-model.trim="partnerQuery" placeholder="상호, 사업자번호, 담당자 검색" @keyup.enter="loadPartners" />
            <button type="button" class="button ghost" @click="loadPartners">검색</button>
          </div>
          <div class="data-list">
            <article v-for="partner in partners" :key="partner.id" class="partner-row">
              <button type="button" class="row-main" @click="editPartner(partner)">
                <strong>{{ partner.name }}</strong>
                <span>{{ partner.business_number || "사업자번호 없음" }}</span>
                <small>{{ [partner.contact_name, partner.phone].filter(Boolean).join(" · ") || "연락처 없음" }}</small>
              </button>
              <button type="button" class="icon-danger" title="숨김" @click="hidePartner(partner)">×</button>
            </article>
            <p v-if="!partners.length && !loading" class="empty">등록된 거래처가 없습니다.</p>
          </div>
        </section>
      </div>
    </template>

    <template v-else-if="activeTab === 'accounts'">
      <section class="panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">01</span>
            <h2>계정과목</h2>
          </div>
          <span class="count">{{ filteredAccounts.length }}개</span>
        </div>
        <div class="account-filters">
          <button
            v-for="type in accountTypes"
            :key="type.value"
            type="button"
            :class="{ active: accountFilter === type.value }"
            @click="accountFilter = type.value"
          >
            {{ type.label }}
          </button>
        </div>
        <div class="account-grid">
          <article v-for="account in filteredAccounts" :key="account.id" class="account-card">
            <span>{{ account.code }}</span>
            <strong>{{ account.name }}</strong>
            <small>{{ accountTypeLabel(account.account_type) }} · {{ account.normal_side === "debit" ? "차변" : "대변" }}</small>
          </article>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 'sales'">
      <div class="sub-tabs">
        <button v-for="tab in saleSubTabs" :key="tab.key" type="button" :class="{ active: saleSubTab === tab.key }" @click="saleSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', 'sale-layout', { single: saleSubTab !== 'overview' }]">
        <section v-if="saleSubTab === 'form' || saleSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>{{ saleForm.id ? "매출 수정" : "매출 등록" }}</h2>
            </div>
            <button v-if="saleForm.id" type="button" class="text-button" @click="resetSaleForm">새 매출</button>
          </div>
          <form class="erp-form" @submit.prevent="saveSale">
            <label>
              <span>연결 작업</span>
              <select v-model="saleForm.work_id" @change="syncSaleWork">
                <option value="">선택 안 함</option>
                <option v-for="work in works" :key="work.id" :value="work.id">
                  {{ work.work_no }} · {{ work.title }}
                </option>
              </select>
            </label>
            <label>
              <span>거래처 *</span>
              <select v-model="saleForm.partner_id" required>
                <option value="">거래처 선택</option>
                <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
              </select>
            </label>
            <label>
              <span>매출일 *</span>
              <input v-model="saleForm.sale_date" type="date" required />
            </label>
            <label>
              <span>상태</span>
              <select v-model="saleForm.invoice_status">
                <option value="draft">미발행</option>
                <option value="cancelled">취소</option>
              </select>
            </label>
            <label class="wide">
              <span>품목명 *</span>
              <input v-model.trim="saleForm.item_name" required maxlength="255" placeholder="예: 네트워크 구축 공사" />
            </label>
            <label>
              <span>수량</span>
              <input v-model="saleForm.quantity" type="number" min="0" step="0.01" />
            </label>
            <label>
              <span>단가</span>
              <input v-model="saleForm.unit_price" type="number" min="0" step="0.01" />
            </label>
            <label class="wide">
              <span>메모</span>
              <textarea v-model.trim="saleForm.memo" rows="3"></textarea>
            </label>
            <div class="sale-preview wide">
              <div><span>공급가</span><strong>{{ money(salePreviewSupply) }}원</strong></div>
              <div><span>부가세</span><strong>{{ money(salePreviewVat) }}원</strong></div>
              <div><span>합계</span><strong>{{ money(salePreviewTotal) }}원</strong></div>
              <p>저장 후 세금계산서 발행 버튼으로 전표까지 자동 생성합니다.</p>
            </div>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetSaleForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : saleForm.id ? "매출 수정" : "매출 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="saleSubTab === 'list' || saleSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>매출 / 세금계산서</h2>
            </div>
            <span class="count">{{ sales.length }}건</span>
          </div>
          <div class="search-row">
            <input v-model.trim="saleQuery" placeholder="품목, 작업번호, 거래처 검색" @keyup.enter="loadSales" />
            <button type="button" class="button ghost" @click="loadSales">검색</button>
          </div>
          <div class="table-scroll">
            <table class="erp-table">
              <thead>
                <tr>
                  <th>매출일</th>
                  <th>품목</th>
                  <th>거래처</th>
                  <th>작업</th>
                  <th class="num">공급가</th>
                  <th class="num">VAT</th>
                  <th class="num">합계</th>
                  <th>상태</th>
                  <th>세금계산서</th>
                  <th>처리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sale in sales" :key="sale.id">
                  <td>{{ sale.sale_date }}</td>
                  <td>{{ sale.item_name }}</td>
                  <td>{{ sale.partner_name }}</td>
                  <td>{{ sale.work_no || "-" }}</td>
                  <td class="num">{{ money(sale.supply_amount) }}</td>
                  <td class="num">{{ money(sale.vat_amount) }}</td>
                  <td class="num strong">{{ money(sale.total_amount) }}</td>
                  <td>{{ saleInvoiceStatusLabel(sale.invoice_status) }}</td>
                  <td>{{ sale.tax_invoice_number || (sale.tax_invoice_id ? `#${sale.tax_invoice_id}` : "-") }}</td>
                  <td>
                    <div class="table-actions">
                      <button v-if="sale.invoice_status === 'draft'" type="button" class="mini-button" @click="editSale(sale)">수정</button>
                      <button v-if="sale.invoice_status === 'draft' && !sale.tax_invoice_id" type="button" class="mini-button primary" @click="issueTaxInvoicePrompt(sale)">발행</button>
                      <button v-if="sale.invoice_status === 'draft'" type="button" class="mini-button danger" @click="cancelSalePrompt(sale)">취소</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!sales.length && !loadingSales" class="empty">등록된 매출이 없습니다.</p>
          </div>

          <div class="panel-heading invoice-heading">
            <div>
              <span class="section-number">03</span>
              <h2>발행 세금계산서</h2>
            </div>
            <span class="count">{{ taxInvoices.length }}건</span>
          </div>
          <div class="table-scroll">
            <table class="erp-table">
              <thead>
                <tr>
                  <th>발행일</th>
                  <th>번호</th>
                  <th>거래처</th>
                  <th>작업</th>
                  <th class="num">공급가</th>
                  <th class="num">VAT</th>
                  <th class="num">합계</th>
                  <th>상태</th>
                  <th>API</th>
                  <th>처리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in taxInvoices" :key="invoice.id">
                  <td>{{ invoice.issue_date }}</td>
                  <td>{{ invoice.invoice_number }}</td>
                  <td>{{ invoice.partner_name }}</td>
                  <td>{{ invoice.work_no || "-" }}</td>
                  <td class="num">{{ money(invoice.supply_amount) }}</td>
                  <td class="num">{{ money(invoice.vat_amount) }}</td>
                  <td class="num strong">{{ money(invoice.total_amount) }}</td>
                  <td>{{ taxInvoiceStatusLabel(invoice.status) }}</td>
                  <td>{{ taxInvoiceApiStatusLabel(invoice.api_status) }}</td>
                  <td>
                    <button v-if="invoice.status === 'issued'" type="button" class="mini-button danger" @click="cancelTaxInvoicePrompt(invoice)">취소</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!taxInvoices.length && !loadingSales" class="empty">발행된 세금계산서가 없습니다.</p>
          </div>

        </section>
      </div>
    </template>

    <template v-else-if="activeTab === 'salesStatus'">
      <div class="sub-tabs">
        <button
          v-for="tab in salesStatusSubTabs"
          :key="tab.key"
          type="button"
          :class="{ active: salesStatusSubTab === tab.key }"
          @click="salesStatusSubTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <section v-if="salesStatusSubTab === 'summary'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">01</span>
            <h2>매출 현황</h2>
          </div>
          <button type="button" class="text-button" @click="refreshSalesStatus">새로고침</button>
        </div>
        <div class="summary-controls">
          <label>
            <span>시작일</span>
            <input v-model="saleSummaryForm.date_from" type="date" />
          </label>
          <label>
            <span>종료일</span>
            <input v-model="saleSummaryForm.date_to" type="date" />
          </label>
          <label>
            <span>거래처</span>
            <select v-model="saleSummaryForm.partner_id">
              <option value="">전체 거래처</option>
              <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
            </select>
          </label>
          <button type="button" class="button primary" @click="refreshSalesStatus">현황 조회</button>
        </div>
        <div v-if="saleSummary" class="summary-box board">
          <div class="summary-total">
            <span>{{ saleSummary.date_from }} ~ {{ saleSummary.date_to }}</span>
            <strong>{{ money(saleSummary.total_amount) }}원</strong>
            <small>공급가 {{ money(saleSummary.supply_amount) }}원 · VAT {{ money(saleSummary.vat_amount) }}원 · {{ saleSummary.sale_count }}건</small>
          </div>
          <div class="summary-columns">
            <div>
              <h3>월별</h3>
              <p v-for="row in saleSummary.month_rows" :key="`status-month-${row.label}`">
                <span>{{ row.label }}</span><strong>{{ money(row.total_amount) }}원</strong>
              </p>
              <p v-if="!saleSummary.month_rows.length" class="empty mini">자료 없음</p>
            </div>
            <div>
              <h3>거래처별</h3>
              <p v-for="row in saleSummary.partner_rows" :key="`status-partner-${row.label}`">
                <span>{{ row.label }}</span><strong>{{ money(row.total_amount) }}원</strong>
              </p>
              <p v-if="!saleSummary.partner_rows.length" class="empty mini">자료 없음</p>
            </div>
            <div>
              <h3>품목별</h3>
              <p v-for="row in saleSummary.item_rows" :key="`status-item-${row.label}`">
                <span>{{ row.label }}</span><strong>{{ money(row.total_amount) }}원</strong>
              </p>
              <p v-if="!saleSummary.item_rows.length" class="empty mini">자료 없음</p>
            </div>
          </div>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'receivables'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">02</span>
            <h2>미수금 현황</h2>
          </div>
          <span class="count">{{ receivableSummary?.sale_count || 0 }}건</span>
        </div>
        <div v-if="receivableSummary" class="summary-box receivable-board">
          <div class="summary-total">
            <span>{{ receivableSummary.date_from }} ~ {{ receivableSummary.date_to }}</span>
            <strong>{{ money(receivableSummary.receivable_amount) }}원</strong>
            <small>매출 {{ money(receivableSummary.total_amount) }}원 · 입금 {{ money(receivableSummary.paid_amount) }}원 · 미수 {{ receivableSummary.sale_count }}건</small>
          </div>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>매출일</th>
                <th>거래처</th>
                <th>품목</th>
                <th>작업</th>
                <th>세금계산서</th>
                <th class="num">매출액</th>
                <th class="num">입금액</th>
                <th class="num">미수금</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in receivableSummary?.rows || []" :key="`receivable-${row.sale_id}`">
                <td>{{ row.sale_date }}</td>
                <td>{{ row.partner_name }}</td>
                <td>{{ row.item_name }}</td>
                <td>{{ row.work_no || "-" }}</td>
                <td>{{ row.tax_invoice_number || "-" }}</td>
                <td class="num">{{ money(row.total_amount) }}</td>
                <td class="num">{{ money(row.paid_amount) }}</td>
                <td class="num strong">{{ money(row.receivable_amount) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!(receivableSummary?.rows || []).length" class="empty">미수금이 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'sales'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">03</span>
            <h2>매출 리스트</h2>
          </div>
          <span class="count">{{ sales.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>매출일</th>
                <th>거래처</th>
                <th>품목</th>
                <th>작업</th>
                <th class="num">공급가</th>
                <th class="num">VAT</th>
                <th class="num">합계</th>
                <th>상태</th>
                <th>전표</th>
                <th>처리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sale in sales" :key="`status-sale-${sale.id}`">
                <td>{{ sale.sale_date }}</td>
                <td>{{ sale.partner_name }}</td>
                <td>{{ sale.item_name }}</td>
                <td>{{ sale.work_no || "-" }}</td>
                <td class="num">{{ money(sale.supply_amount) }}</td>
                <td class="num">{{ money(sale.vat_amount) }}</td>
                <td class="num strong">{{ money(sale.total_amount) }}</td>
                <td>{{ saleInvoiceStatusLabel(sale.invoice_status) }}</td>
                <td>{{ sale.journal_entry_id ? `#${sale.journal_entry_id}` : "-" }}</td>
                <td>
                  <div class="table-actions">
                    <button v-if="sale.invoice_status === 'draft'" type="button" class="mini-button primary" @click="issueTaxInvoicePrompt(sale)">세금계산서</button>
                    <button v-if="sale.invoice_status === 'draft'" type="button" class="mini-button danger" @click="cancelSalePrompt(sale)">매출취소</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!sales.length && !loadingSales" class="empty">매출 자료가 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'invoices'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">04</span>
            <h2>세금계산서 리스트</h2>
          </div>
          <span class="count">{{ taxInvoices.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>발행일</th>
                <th>번호</th>
                <th>거래처</th>
                <th class="num">합계</th>
                <th>상태</th>
                <th>API</th>
                <th>발행전표</th>
                <th>취소전표</th>
                <th>처리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invoice in taxInvoices" :key="`status-tax-${invoice.id}`">
                <td>{{ invoice.issue_date }}</td>
                <td>{{ invoice.invoice_number }}</td>
                <td>{{ invoice.partner_name }}</td>
                <td class="num strong">{{ money(invoice.total_amount) }}</td>
                <td>{{ taxInvoiceStatusLabel(invoice.status) }}</td>
                <td>{{ taxInvoiceApiStatusLabel(invoice.api_status) }}</td>
                <td>{{ invoice.journal_entry_id ? `#${invoice.journal_entry_id}` : "-" }}</td>
                <td>{{ invoice.reversal_journal_entry_id ? `#${invoice.reversal_journal_entry_id}` : "-" }}</td>
                <td>
                  <button v-if="invoice.status === 'issued'" type="button" class="mini-button danger" @click="cancelTaxInvoicePrompt(invoice)">취소</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!taxInvoices.length && !loadingSales" class="empty">세금계산서 자료가 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'payments'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">06</span>
            <h2>입금 현황</h2>
          </div>
          <span class="count">{{ payments.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>입금일</th>
                <th>거래처</th>
                <th>작업</th>
                <th>연결 매출</th>
                <th>계좌/메모</th>
                <th class="num">금액</th>
                <th>상태</th>
                <th>전표</th>
                <th>취소전표</th>
                <th>처리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in payments" :key="`status-payment-${payment.id}`">
                <td>{{ payment.payment_date }}</td>
                <td>{{ payment.partner_name }}</td>
                <td>{{ payment.work_no || "-" }}</td>
                <td>{{ payment.related_sale_no || "-" }}</td>
                <td>{{ payment.bank_account || "-" }}</td>
                <td class="num strong">{{ money(payment.amount) }}</td>
                <td>{{ paymentStatusLabel(payment.status) }}</td>
                <td>{{ payment.journal_entry_id ? `#${payment.journal_entry_id}` : "-" }}</td>
                <td>{{ payment.reversal_journal_entry_id ? `#${payment.reversal_journal_entry_id}` : "-" }}</td>
                <td>
                  <button v-if="payment.status !== 'cancelled'" type="button" class="mini-button danger" @click="cancelPaymentPrompt(payment)">취소</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!payments.length && !loadingPayments" class="empty">입금 자료가 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'expenses'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">07</span>
            <h2>비용 현황</h2>
          </div>
          <span class="count">{{ expenses.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>비용일</th>
                <th>거래처</th>
                <th>항목</th>
                <th>계정과목</th>
                <th>결제수단</th>
                <th class="num">공급가</th>
                <th class="num">VAT</th>
                <th class="num">합계</th>
                <th>상태</th>
                <th>전표</th>
                <th>처리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="expense in expenses" :key="`status-expense-${expense.id}`">
                <td>{{ expense.expense_date }}</td>
                <td>{{ expense.partner_name || "-" }}</td>
                <td>{{ expense.item_name }}</td>
                <td>{{ expense.account_code }} {{ expense.account_name }}</td>
                <td>{{ expensePaymentMethodLabel(expense.payment_method) }}</td>
                <td class="num">{{ money(expense.supply_amount) }}</td>
                <td class="num">{{ money(expense.vat_amount) }}</td>
                <td class="num strong">{{ money(expense.total_amount) }}</td>
                <td>{{ expenseStatusLabel(expense.status) }}</td>
                <td>{{ expense.journal_entry_id ? `#${expense.journal_entry_id}` : "-" }}</td>
                <td>
                  <button v-if="expense.status !== 'cancelled'" type="button" class="mini-button danger" @click="cancelExpensePrompt(expense)">취소</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!expenses.length && !loadingExpenses" class="empty">비용 자료가 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'journals'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">08</span>
            <h2>전표 현황</h2>
          </div>
          <span class="count">{{ journals.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table">
            <thead>
              <tr>
                <th>전표일</th>
                <th>적요</th>
                <th>거래처</th>
                <th>작업</th>
                <th>원천</th>
                <th class="num">차변</th>
                <th class="num">대변</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="journal in journals" :key="`status-journal-${journal.id}`">
                <td>{{ journal.entry_date }}</td>
                <td>{{ journal.description }}</td>
                <td>{{ journal.partner_name || "-" }}</td>
                <td>{{ journal.work_no || "-" }}</td>
                <td>{{ journal.source_type }} {{ journal.source_id || "" }}</td>
                <td class="num">{{ money(journal.debit_total) }}</td>
                <td class="num">{{ money(journal.credit_total) }}</td>
                <td>{{ journal.status }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!journals.length && !loading" class="empty">전표 자료가 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'cancellations'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">05</span>
            <h2>취소 / 정정 이력</h2>
          </div>
          <span class="count">매출 {{ cancelledSales.length }}건 · 세금계산서 {{ cancelledTaxInvoices.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table muted-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>일자</th>
                <th>대상</th>
                <th>거래처</th>
                <th class="num">금액</th>
                <th>취소일</th>
                <th>사유</th>
                <th>취소전표</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sale in cancelledSales" :key="`status-cancel-sale-${sale.id}`">
                <td>매출</td>
                <td>{{ sale.sale_date }}</td>
                <td>{{ sale.item_name }}</td>
                <td>{{ sale.partner_name }}</td>
                <td class="num">{{ money(sale.total_amount) }}</td>
                <td>{{ compactDate(sale.cancelled_at) || "-" }}</td>
                <td>{{ sale.cancel_reason || "-" }}</td>
                <td>-</td>
              </tr>
              <tr v-for="invoice in cancelledTaxInvoices" :key="`status-cancel-tax-${invoice.id}`">
                <td>세금계산서</td>
                <td>{{ invoice.issue_date }}</td>
                <td>{{ invoice.invoice_number }}</td>
                <td>{{ invoice.partner_name }}</td>
                <td class="num">{{ money(invoice.total_amount) }}</td>
                <td>{{ compactDate(invoice.cancelled_at) || "-" }}</td>
                <td>{{ invoice.cancel_reason || "-" }}</td>
                <td>{{ invoice.reversal_journal_entry_id ? `#${invoice.reversal_journal_entry_id}` : "-" }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!cancelledSales.length && !cancelledTaxInvoices.length" class="empty">취소/정정 이력이 없습니다.</p>
        </div>
      </section>

      <section v-if="salesStatusSubTab === 'audit'" class="panel status-panel">
        <div class="panel-heading">
          <div>
            <span class="section-number">10</span>
            <h2>감사 로그</h2>
          </div>
          <span class="count">{{ auditLogs.length }}건</span>
        </div>
        <div class="table-scroll status-table">
          <table class="erp-table muted-table">
            <thead>
              <tr>
                <th>일시</th>
                <th>사용자</th>
                <th>행위</th>
                <th>대상</th>
                <th>작업</th>
                <th>거래처</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in auditLogs" :key="`audit-${log.id}`">
                <td>{{ compactDate(log.created_at) }}</td>
                <td>{{ log.actor_username || log.actor_user_id || "-" }}</td>
                <td>{{ auditActionLabel(log.action) }}</td>
                <td>{{ auditEntityLabel(log.entity_type) }} {{ log.entity_id ? `#${log.entity_id}` : "" }}</td>
                <td>{{ log.work_id ? `#${log.work_id}` : "-" }}</td>
                <td>{{ log.partner_id ? `#${log.partner_id}` : "-" }}</td>
                <td>{{ log.description || "-" }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!auditLogs.length && !loading" class="empty">감사 로그가 없습니다.</p>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 'payments'">
      <div class="sub-tabs">
        <button v-for="tab in paymentSubTabs" :key="tab.key" type="button" :class="{ active: paymentSubTab === tab.key }" @click="paymentSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', 'payment-layout', { single: paymentSubTab !== 'overview' }]">
        <section v-if="paymentSubTab === 'form' || paymentSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>입금 등록</h2>
            </div>
          </div>
          <form class="erp-form" @submit.prevent="savePayment">
            <label>
              <span>연결 작업</span>
              <select v-model="paymentForm.work_id" @change="syncPaymentWork">
                <option value="">선택 안 함</option>
                <option v-for="work in works" :key="work.id" :value="work.id">
                  {{ work.work_no }} · {{ work.title }}
                </option>
              </select>
            </label>
            <label>
              <span>거래처 *</span>
              <select v-model="paymentForm.partner_id" required>
                <option value="">거래처 선택</option>
                <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
              </select>
            </label>
            <label>
              <span>입금일 *</span>
              <input v-model="paymentForm.payment_date" type="date" required />
            </label>
            <label>
              <span>입금액 *</span>
              <input v-model="paymentForm.amount" type="number" min="0" step="0.01" required />
            </label>
            <label class="wide">
              <span>연결 매출</span>
              <select v-model="paymentForm.related_sale_id">
                <option value="">선택 안 함</option>
                <option v-for="sale in sales" :key="sale.id" :value="sale.id">
                  {{ sale.sale_date }} · {{ sale.item_name }} · {{ money(sale.total_amount) }}원
                </option>
              </select>
            </label>
            <label class="wide">
              <span>입금 계좌/메모</span>
              <input v-model.trim="paymentForm.bank_account" maxlength="120" placeholder="예: 국민은행 123-456-789" />
            </label>
            <label class="wide">
              <span>메모</span>
              <textarea v-model.trim="paymentForm.memo" rows="3"></textarea>
            </label>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetPaymentForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : "입금 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="paymentSubTab === 'list' || paymentSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>입금 내역</h2>
            </div>
            <span class="count">{{ payments.length }}건</span>
          </div>
          <div class="search-row">
            <input v-model.trim="paymentQuery" placeholder="거래처, 작업번호, 계좌 검색" @keyup.enter="loadPayments" />
            <button type="button" class="button ghost" @click="loadPayments">검색</button>
          </div>
          <div class="journal-list">
            <article v-for="payment in payments" :key="payment.id" class="journal-card">
              <header>
                <div>
                  <span>{{ payment.payment_date }}</span>
                  <strong>{{ payment.partner_name }}</strong>
                  <small>{{ payment.work_no || "작업 없음" }} · {{ payment.bank_account || "계좌 메모 없음" }}</small>
                </div>
                <b>{{ money(payment.amount) }}원</b>
              </header>
              <div class="journal-detail">
                <div>
                  <span>연결 매출</span>
                  <span>{{ payment.related_sale_no || "없음" }}</span>
                </div>
                <div>
                  <span>전표</span>
                  <span>{{ payment.journal_entry_id ? `#${payment.journal_entry_id}` : "없음" }}</span>
                </div>
                <div>
                  <span>상태</span>
                  <span>{{ paymentStatusLabel(payment.status) }}</span>
                </div>
                <div v-if="payment.reversal_journal_entry_id">
                  <span>취소전표</span>
                  <span>#{{ payment.reversal_journal_entry_id }}</span>
                </div>
                <div v-if="payment.cancel_reason">
                  <span>취소사유</span>
                  <span>{{ payment.cancel_reason }}</span>
                </div>
                <div v-for="allocation in payment.allocations" :key="allocation.id">
                  <span>{{ allocation.sale_no }}</span>
                  <span>{{ money(allocation.amount) }}원</span>
                </div>
              </div>
              <div class="journal-actions">
                <button
                  v-if="payment.status !== 'cancelled'"
                  type="button"
                  class="button ghost danger"
                  @click="cancelPaymentPrompt(payment)"
                >
                  입금 취소
                </button>
              </div>
            </article>
            <p v-if="!payments.length && !loadingPayments" class="empty">등록된 입금이 없습니다.</p>
          </div>
        </section>
      </div>
    </template>

    <template v-else-if="activeTab === 'expenses'">
      <div class="sub-tabs">
        <button v-for="tab in expenseSubTabs" :key="tab.key" type="button" :class="{ active: expenseSubTab === tab.key }" @click="expenseSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', 'expense-layout', { single: expenseSubTab !== 'overview' }]">
        <section v-if="expenseSubTab === 'form' || expenseSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>비용 등록</h2>
            </div>
          </div>
          <form class="erp-form" @submit.prevent="saveExpense">
            <label>
              <span>연결 작업</span>
              <select v-model="expenseForm.work_id">
                <option value="">선택 안 함</option>
                <option v-for="work in works" :key="work.id" :value="work.id">
                  {{ work.work_no }} · {{ work.title }}
                </option>
              </select>
            </label>
            <label>
              <span>거래처</span>
              <select v-model="expenseForm.partner_id">
                <option value="">선택 안 함</option>
                <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
              </select>
            </label>
            <label>
              <span>비용일 *</span>
              <input v-model="expenseForm.expense_date" type="date" required />
            </label>
            <label>
              <span>계정과목 *</span>
              <select v-model="expenseForm.account_id" required>
                <option value="">비용 계정 선택</option>
                <option v-for="account in expenseAccounts" :key="account.id" :value="account.id">
                  {{ account.code }} {{ account.name }}
                </option>
              </select>
            </label>
            <label>
              <span>결제수단 *</span>
              <select v-model="expenseForm.payment_method" required>
                <option value="business_account">사업용 통장</option>
                <option value="business_card">사업용 카드</option>
                <option value="personal_money">개인 돈</option>
                <option value="cash">현금</option>
              </select>
            </label>
            <label class="wide">
              <span>항목명 *</span>
              <input v-model.trim="expenseForm.item_name" required maxlength="255" placeholder="예: 랜 케이블 구입" />
            </label>
            <label>
              <span>공급가 *</span>
              <input v-model="expenseForm.supply_amount" type="number" min="0" step="0.01" required />
            </label>
            <label>
              <span>부가세</span>
              <input v-model="expenseForm.vat_amount" type="number" min="0" step="0.01" />
            </label>
            <label class="wide">
              <span>메모</span>
              <textarea v-model.trim="expenseForm.memo" rows="3" placeholder="영수증은 작업 문서함에서 영수증 유형으로 첨부하세요."></textarea>
            </label>
            <div class="sale-preview wide">
              <div><span>공급가</span><strong>{{ money(expensePreviewSupply) }}원</strong></div>
              <div><span>부가세</span><strong>{{ money(expensePreviewVat) }}원</strong></div>
              <div><span>합계</span><strong>{{ money(expensePreviewTotal) }}원</strong></div>
              <p>저장 시 비용/부가세/결제수단 전표가 자동 생성됩니다.</p>
            </div>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetExpenseForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : "비용 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="expenseSubTab === 'list' || expenseSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>비용 내역</h2>
            </div>
            <span class="count">{{ expenses.length }}건</span>
          </div>
          <div class="search-row">
            <input v-model.trim="expenseQuery" placeholder="항목, 거래처, 작업번호, 계정 검색" @keyup.enter="loadExpenses" />
            <button type="button" class="button ghost" @click="loadExpenses">검색</button>
          </div>
          <div class="journal-list">
            <article v-for="expense in expenses" :key="expense.id" class="journal-card">
              <header>
                <div>
                  <span>{{ expense.expense_date }}</span>
                  <strong>{{ expense.item_name }}</strong>
                  <small>{{ expense.partner_name || "거래처 없음" }} · {{ expense.work_no || "작업 없음" }}</small>
                </div>
                <b>{{ money(expense.total_amount) }}원</b>
              </header>
              <div class="journal-detail">
                <div>
                  <span>계정과목</span>
                  <span>{{ expense.account_code }} {{ expense.account_name }}</span>
                </div>
                <div>
                  <span>공급가/VAT</span>
                  <span>{{ money(expense.supply_amount) }} / {{ money(expense.vat_amount) }}</span>
                </div>
                <div>
                  <span>결제수단</span>
                  <span>{{ expensePaymentMethodLabel(expense.payment_method) }}</span>
                </div>
                <div>
                  <span>상태</span>
                  <span>{{ expenseStatusLabel(expense.status) }}</span>
                </div>
                <div>
                  <span>전표</span>
                  <span>{{ expense.journal_entry_id ? `#${expense.journal_entry_id}` : "없음" }}</span>
                </div>
                <div v-if="expense.reversal_journal_entry_id">
                  <span>취소전표</span>
                  <span>#{{ expense.reversal_journal_entry_id }}</span>
                </div>
                <div v-if="expense.cancel_reason">
                  <span>취소사유</span>
                  <span>{{ expense.cancel_reason }}</span>
                </div>
              </div>
              <div class="journal-actions">
                <button
                  v-if="expense.status !== 'cancelled'"
                  type="button"
                  class="button ghost danger"
                  @click="cancelExpensePrompt(expense)"
                >
                  비용 취소
                </button>
              </div>
            </article>
            <p v-if="!expenses.length && !loadingExpenses" class="empty">등록된 비용이 없습니다.</p>
          </div>
        </section>
      </div>
    </template>

    <template v-else-if="activeTab === 'assets'">
      <div class="sub-tabs">
        <button v-for="tab in assetSubTabs" :key="tab.key" type="button" :class="{ active: assetSubTab === tab.key }" @click="assetSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['split-layout', 'asset-layout', { single: assetSubTab !== 'overview' }]">
        <section v-if="assetSubTab === 'form' || assetSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>자산 등록</h2>
            </div>
          </div>
          <form class="erp-form" @submit.prevent="saveAsset">
            <label>
              <span>연결 작업</span>
              <select v-model="assetForm.work_id">
                <option value="">선택 안 함</option>
                <option v-for="work in works" :key="work.id" :value="work.id">
                  {{ work.work_no }} · {{ work.title }}
                </option>
              </select>
            </label>
            <label>
              <span>구매처</span>
              <select v-model="assetForm.partner_id">
                <option value="">선택 안 함</option>
                <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
              </select>
            </label>
            <label>
              <span>자산 계정 *</span>
              <select v-model="assetForm.asset_account_id" required>
                <option value="">자산 계정 선택</option>
                <option v-for="account in assetAccounts" :key="account.id" :value="account.id">
                  {{ account.code }} {{ account.name }}
                </option>
              </select>
            </label>
            <label>
              <span>분류 *</span>
              <select v-model="assetForm.category" required>
                <option value="fixture">비품</option>
                <option value="server">서버</option>
                <option value="computer">컴퓨터</option>
                <option value="network">네트워크장비</option>
                <option value="vehicle">차량</option>
                <option value="etc">기타</option>
              </select>
            </label>
            <label class="wide">
              <span>자산명 *</span>
              <input v-model.trim="assetForm.asset_name" required maxlength="255" placeholder="예: Mac Studio" />
            </label>
            <label>
              <span>취득일 *</span>
              <input v-model="assetForm.purchase_date" type="date" required />
            </label>
            <label>
              <span>취득금액 *</span>
              <input v-model="assetForm.purchase_amount" type="number" min="0" step="0.01" required />
            </label>
            <label>
              <span>내용연수 *</span>
              <input v-model="assetForm.useful_life_years" type="number" min="1" max="50" step="1" required />
            </label>
            <label>
              <span>취득방법 *</span>
              <select v-model="assetForm.acquisition_method" required>
                <option value="business_account">사업용 통장</option>
                <option value="business_card">사업용 카드</option>
                <option value="personal_money">개인 돈</option>
                <option value="cash">현금</option>
              </select>
            </label>
            <label class="wide">
              <span>메모</span>
              <textarea v-model.trim="assetForm.memo" rows="3" placeholder="구매영수증/제품사진은 작업 문서함에서 첨부하세요."></textarea>
            </label>
            <div class="sale-preview wide">
              <div><span>연 상각 예상</span><strong>{{ money(assetAnnualDepreciation) }}원</strong></div>
              <div><span>취득금액</span><strong>{{ money(assetForm.purchase_amount) }}원</strong></div>
              <p>저장 시 자산 취득 전표가 자동 생성됩니다.</p>
            </div>
            <div class="form-actions wide">
              <button type="button" class="button ghost" @click="resetAssetForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving">
                {{ saving ? "저장 중" : "자산 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="assetSubTab === 'list' || assetSubTab === 'overview'" class="panel list-panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>자산 내역</h2>
            </div>
            <span class="count">{{ assets.length }}건</span>
          </div>
          <div class="search-row">
            <input v-model.trim="assetQuery" placeholder="자산명, 거래처, 작업번호, 계정 검색" @keyup.enter="loadAssets" />
            <button type="button" class="button ghost" @click="loadAssets">검색</button>
          </div>
          <div class="journal-list">
            <article v-for="asset in assets" :key="asset.id" class="journal-card">
              <header>
                <div>
                  <span>{{ asset.purchase_date }}</span>
                  <strong>{{ asset.asset_name }}</strong>
                  <small>{{ asset.partner_name || "거래처 없음" }} · {{ asset.work_no || "작업 없음" }}</small>
                </div>
                <b>{{ money(asset.current_value) }}원</b>
              </header>
              <div class="journal-detail">
                <div>
                  <span>자산계정</span>
                  <span>{{ asset.asset_account_code }} {{ asset.asset_account_name }}</span>
                </div>
                <div>
                  <span>취득/누계</span>
                  <span>{{ money(asset.purchase_amount) }} / {{ money(asset.accumulated_depreciation) }}</span>
                </div>
                <div>
                  <span>내용연수</span>
                  <span>{{ asset.useful_life_years }}년</span>
                </div>
                <div>
                  <span>전표</span>
                  <span>{{ asset.journal_entry_id ? `#${asset.journal_entry_id}` : "없음" }}</span>
                </div>
              </div>
              <div class="journal-actions">
                <button
                  v-if="Number(asset.current_value) > 0"
                  type="button"
                  class="button ghost"
                  @click="depreciateAssetPrompt(asset)"
                >
                  감가상각
                </button>
              </div>
            </article>
            <p v-if="!assets.length && !loadingAssets" class="empty">등록된 자산이 없습니다.</p>
          </div>
        </section>
      </div>
    </template>

    <template v-else>
      <div class="sub-tabs">
        <button v-for="tab in journalSubTabs" :key="tab.key" type="button" :class="{ active: journalSubTab === tab.key }" @click="journalSubTab = tab.key">
          {{ tab.label }}
        </button>
      </div>
      <div :class="['journal-layout', { single: journalSubTab !== 'overview' }]">
        <section v-if="journalSubTab === 'form' || journalSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">01</span>
              <h2>수동 전표 입력</h2>
            </div>
          </div>
          <form class="journal-form" @submit.prevent="saveJournal">
            <div class="journal-basics">
              <label>
                <span>전표일 *</span>
                <input v-model="journalForm.entry_date" type="date" required />
              </label>
              <label>
                <span>거래처</span>
                <select v-model="journalForm.partner_id">
                  <option value="">선택 안 함</option>
                  <option v-for="partner in partners" :key="partner.id" :value="partner.id">{{ partner.name }}</option>
                </select>
              </label>
              <label>
                <span>연결 작업</span>
                <select v-model="journalForm.work_id" @change="syncJournalWork">
                  <option value="">선택 안 함</option>
                  <option v-for="work in works" :key="work.id" :value="work.id">
                    {{ work.work_no }} · {{ work.title }}
                  </option>
                </select>
              </label>
              <label class="wide">
                <span>적요 *</span>
                <input v-model.trim="journalForm.description" required maxlength="500" placeholder="거래 내용을 입력하세요" />
              </label>
            </div>
            <div class="journal-lines">
              <div class="line-head">
                <span>계정과목</span><span>차변</span><span>대변</span><span>메모</span><span></span>
              </div>
              <div v-for="(line, index) in journalForm.lines" :key="index" class="line-row">
                <select v-model="line.account_id" required>
                  <option value="">계정 선택</option>
                  <option v-for="account in accounts" :key="account.id" :value="account.id">
                    {{ account.code }} {{ account.name }}
                  </option>
                </select>
                <input v-model="line.debit" type="number" min="0" step="0.01" placeholder="0" @input="clearOpposite(line, 'debit')" />
                <input v-model="line.credit" type="number" min="0" step="0.01" placeholder="0" @input="clearOpposite(line, 'credit')" />
                <input v-model.trim="line.memo" maxlength="500" placeholder="선택" />
                <button type="button" class="line-remove" :disabled="journalForm.lines.length <= 2" @click="removeLine(index)">×</button>
              </div>
            </div>
            <button type="button" class="button ghost add-line" @click="addLine">+ 분개선 추가</button>
            <div class="balance-box" :class="{ balanced: isBalanced && debitTotal > 0 }">
              <div><span>차변 합계</span><strong>{{ money(debitTotal) }}원</strong></div>
              <div><span>대변 합계</span><strong>{{ money(creditTotal) }}원</strong></div>
              <p>{{ isBalanced && debitTotal > 0 ? "차변과 대변이 일치합니다." : `차액 ${money(balanceDifference)}원` }}</p>
            </div>
            <div class="form-actions">
              <button type="button" class="button ghost" @click="resetJournalForm">초기화</button>
              <button type="submit" class="button primary" :disabled="saving || !isBalanced || debitTotal <= 0">
                {{ saving ? "저장 중" : "전표 저장" }}
              </button>
            </div>
          </form>
        </section>

        <section v-if="journalSubTab === 'list' || journalSubTab === 'overview'" class="panel">
          <div class="panel-heading">
            <div>
              <span class="section-number">02</span>
              <h2>전표 내역</h2>
            </div>
            <button type="button" class="text-button" @click="loadJournals">새로고침</button>
          </div>
          <div class="journal-list">
            <article v-for="journal in journals" :key="journal.id" class="journal-card">
              <header>
                <div>
                  <span>{{ journal.entry_date }}</span>
                  <strong>{{ journal.description }}</strong>
                  <small>{{ journal.partner_name || "거래처 없음" }}</small>
                </div>
                <b>{{ money(journal.debit_total) }}원</b>
              </header>
              <div class="journal-detail">
                <div v-for="line in journal.lines" :key="line.id">
                  <span>{{ line.account_code }} {{ line.account_name }}</span>
                  <span v-if="Number(line.debit)">차변 {{ money(line.debit) }}</span>
                  <span v-else>대변 {{ money(line.credit) }}</span>
                </div>
              </div>
            </article>
            <p v-if="!journals.length && !loading" class="empty">등록된 전표가 없습니다.</p>
          </div>
        </section>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import api from "@/api";

const tabs = [
  { key: "works", label: "작업 문서 허브" },
  { key: "partners", label: "거래처 관리" },
  { key: "accounts", label: "계정과목" },
  { key: "sales", label: "매출 / 세금계산서" },
  { key: "salesStatus", label: "현황 조회" },
  { key: "payments", label: "입금 관리" },
  { key: "expenses", label: "비용 / 영수증" },
  { key: "assets", label: "자산 관리" },
  { key: "journals", label: "전표 관리" },
];
const twoPaneSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "등록/수정" },
  { key: "list", label: "목록" },
];
const workSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "작업 등록/수정" },
  { key: "list", label: "작업 목록" },
  { key: "documents", label: "문서함" },
];
const partnerSubTabs = twoPaneSubTabs;
const saleSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "매출 등록/수정" },
  { key: "list", label: "발행/처리" },
];
const paymentSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "입금 등록" },
  { key: "list", label: "입금 내역" },
];
const expenseSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "비용 등록" },
  { key: "list", label: "비용 내역" },
];
const assetSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "자산 등록" },
  { key: "list", label: "자산 내역" },
];
const journalSubTabs = [
  { key: "overview", label: "전체" },
  { key: "form", label: "수동 전표 입력" },
  { key: "list", label: "전표 내역" },
];
const salesStatusSubTabs = [
  { key: "summary", label: "매출 요약" },
  { key: "receivables", label: "미수금" },
  { key: "sales", label: "매출 리스트" },
  { key: "invoices", label: "세금계산서" },
  { key: "payments", label: "입금" },
  { key: "expenses", label: "비용" },
  { key: "journals", label: "전표" },
  { key: "cancellations", label: "취소/정정" },
  { key: "audit", label: "감사 로그" },
];
const accountTypes = [
  { value: "", label: "전체" },
  { value: "asset", label: "자산" },
  { value: "liability", label: "부채" },
  { value: "equity", label: "자본" },
  { value: "revenue", label: "수익" },
  { value: "expense", label: "비용" },
];
const documentTypes = [
  { value: "", label: "전체 문서", icon: "▦" },
  { value: "estimate_pdf", label: "견적서", icon: "견" },
  { value: "contract", label: "계약서", icon: "계" },
  { value: "work_photo", label: "작업사진", icon: "사" },
  { value: "receipt", label: "영수증", icon: "영" },
  { value: "tax_invoice", label: "세금계산서", icon: "세" },
  { value: "payment", label: "입금자료", icon: "입" },
  { value: "invoice_pdf", label: "거래명세서", icon: "명" },
  { value: "manual", label: "매뉴얼", icon: "매" },
  { value: "other", label: "기타", icon: "기" },
];

const activeTab = ref("works");
const workSubTab = ref("overview");
const partnerSubTab = ref("overview");
const saleSubTab = ref("overview");
const paymentSubTab = ref("overview");
const expenseSubTab = ref("overview");
const assetSubTab = ref("overview");
const journalSubTab = ref("overview");
const salesStatusSubTab = ref("summary");
const health = reactive({ ok: false, partners: 0, accounts: 0 });
const partners = ref([]);
const accounts = ref([]);
const journals = ref([]);
const works = ref([]);
const estimates = ref([]);
const sales = ref([]);
const saleSummary = ref(null);
const receivableSummary = ref(null);
const taxInvoices = ref([]);
const cancelledSales = ref([]);
const cancelledTaxInvoices = ref([]);
const auditLogs = ref([]);
const payments = ref([]);
const expenses = ref([]);
const assets = ref([]);
const estimateQuery = ref("");
const selectedWork = ref(null);
const workDeleteBlockers = ref(null);
const documents = ref([]);
const mailHistories = ref([]);
const thumbnailUrls = ref({});
const documentFilter = ref("");
const loadingDocuments = ref(false);
const loadingMailHistories = ref(false);
const loadingSales = ref(false);
const loadingPayments = ref(false);
const loadingExpenses = ref(false);
const loadingAssets = ref(false);
const uploading = ref(false);
const uploadProgress = ref("");
const uploadFiles = ref([]);
const fileInputKey = ref(0);
const uploadForm = reactive({ document_type: "contract", title: "" });
const mailForm = reactive({ recipient: "", cc: "", subject: "", body: "" });
const partnerQuery = ref("");
const workQuery = ref("");
const saleQuery = ref("");
const includeCancelledSales = ref(false);
const paymentQuery = ref("");
const expenseQuery = ref("");
const assetQuery = ref("");
const accountFilter = ref("");
const loading = ref(false);
const saving = ref(false);
const notice = reactive({ text: "", type: "success" });

function blankPartner() {
  return {
    id: null, name: "", business_number: "", ceo_name: "", business_type: "",
    business_item: "", contact_name: "", email: "", phone: "", address: "", memo: "",
  };
}
function localDate() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function firstDayOfMonth() {
  return `${localDate().slice(0, 8)}01`;
}
function blankLine() {
  return { account_id: "", debit: "", credit: "", memo: "" };
}
function blankJournal() {
  return { entry_date: localDate(), partner_id: "", work_id: "", description: "", lines: [blankLine(), blankLine()] };
}
function blankWork() {
  return {
    id: null, work_no: "", partner_id: "", source_estimate_record_id: "",
    worker_id: null, work_date: localDate(), status: "planned", title: "", memo: "",
  };
}
function blankSale() {
  return {
    id: null,
    work_id: "",
    partner_id: "",
    sale_date: localDate(),
    item_name: "",
    quantity: "1",
    unit_price: "0",
    invoice_status: "draft",
    memo: "",
  };
}
function blankPayment() {
  return {
    id: null,
    work_id: "",
    partner_id: "",
    payment_date: localDate(),
    amount: "0",
    bank_account: "",
    related_sale_id: "",
    memo: "",
  };
}
function blankExpense() {
  return {
    id: null,
    work_id: "",
    partner_id: "",
    expense_date: localDate(),
    account_id: "",
    item_name: "",
    supply_amount: "0",
    vat_amount: "0",
    payment_method: "business_account",
    memo: "",
  };
}
function blankAsset() {
  return {
    id: null,
    work_id: "",
    partner_id: "",
    asset_account_id: "",
    asset_name: "",
    category: "fixture",
    purchase_date: localDate(),
    purchase_amount: "0",
    useful_life_years: "5",
    acquisition_method: "business_account",
    memo: "",
  };
}

const partnerForm = reactive(blankPartner());
const journalForm = reactive(blankJournal());
const workForm = reactive(blankWork());
const saleForm = reactive(blankSale());
const saleSummaryForm = reactive({ date_from: firstDayOfMonth(), date_to: localDate(), partner_id: "" });
const paymentForm = reactive(blankPayment());
const expenseForm = reactive(blankExpense());
const assetForm = reactive(blankAsset());
const filteredAccounts = computed(() =>
  accountFilter.value ? accounts.value.filter((item) => item.account_type === accountFilter.value) : accounts.value
);
const expenseAccounts = computed(() => accounts.value.filter((item) => item.account_type === "expense" && item.is_active));
const assetAccounts = computed(() =>
  accounts.value.filter((item) => ["150", "151", "152", "153"].includes(item.code) && item.is_active)
);
const filteredDocuments = computed(() =>
  documentFilter.value
    ? documents.value.filter((item) => item.document_type === documentFilter.value)
    : documents.value
);
const photoDocuments = computed(() => documents.value.filter((item) => item.document_type === "work_photo"));
const selectedEstimate = computed(() =>
  estimates.value.find((item) => String(item.id) === String(workForm.source_estimate_record_id)) || null
);
const debitTotal = computed(() => journalForm.lines.reduce((sum, line) => sum + Number(line.debit || 0), 0));
const creditTotal = computed(() => journalForm.lines.reduce((sum, line) => sum + Number(line.credit || 0), 0));
const balanceDifference = computed(() => Math.abs(debitTotal.value - creditTotal.value));
const isBalanced = computed(() => Math.abs(debitTotal.value - creditTotal.value) < 0.005);
const salePreviewSupply = computed(() => Number(saleForm.quantity || 0) * Number(saleForm.unit_price || 0));
const salePreviewVat = computed(() => Math.round(salePreviewSupply.value * 0.1 * 100) / 100);
const salePreviewTotal = computed(() => salePreviewSupply.value + salePreviewVat.value);
const expensePreviewSupply = computed(() => Number(expenseForm.supply_amount || 0));
const expensePreviewVat = computed(() => Number(expenseForm.vat_amount || 0));
const expensePreviewTotal = computed(() => expensePreviewSupply.value + expensePreviewVat.value);
const assetAnnualDepreciation = computed(() => {
  const years = Number(assetForm.useful_life_years || 1);
  return years > 0 ? Number(assetForm.purchase_amount || 0) / years : 0;
});

onMounted(async () => {
  await Promise.all([loadHealth(), loadPartners(), loadAccounts(), loadWorks(), loadEstimates()]);
});

function showNotice(text, type = "success") {
  notice.text = text;
  notice.type = type;
  window.setTimeout(() => {
    if (notice.text === text) notice.text = "";
  }, 4000);
}
function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(", ");
  return typeof detail === "string" ? detail : fallback;
}
async function loadHealth() {
  try {
    Object.assign(health, (await api.get("/erp/health")).data);
  } catch (error) {
    health.ok = false;
  }
}
async function loadPartners() {
  loading.value = true;
  try {
    partners.value = (await api.get("/erp/partners", { params: { q: partnerQuery.value || undefined, limit: 500 } })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "거래처를 불러오지 못했습니다."), "error");
  } finally {
    loading.value = false;
  }
}
async function loadAccounts() {
  try {
    accounts.value = (await api.get("/erp/accounts")).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "계정과목을 불러오지 못했습니다."), "error");
  }
}
async function loadJournals() {
  loading.value = true;
  try {
    const params = { limit: 200 };
    if (activeTab.value === "salesStatus") {
      params.date_from = saleSummaryForm.date_from || undefined;
      params.date_to = saleSummaryForm.date_to || undefined;
    }
    journals.value = (await api.get("/erp/journals", { params })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "전표를 불러오지 못했습니다."), "error");
  } finally {
    loading.value = false;
  }
}
async function loadSales(useQuery = true) {
  loadingSales.value = true;
  try {
    const params = { limit: 200, include_cancelled: false };
    if (useQuery && saleQuery.value) params.q = saleQuery.value;
    if (activeTab.value === "salesStatus") {
      params.date_from = saleSummaryForm.date_from || undefined;
      params.date_to = saleSummaryForm.date_to || undefined;
      params.partner_id = saleSummaryForm.partner_id || undefined;
    }
    sales.value = (await api.get("/erp/sales", { params })).data || [];
    taxInvoices.value = (await api.get("/erp/tax-invoices", { params })).data || [];
    if (includeCancelledSales.value) {
      const archiveParams = { ...params, include_cancelled: true };
      const allSales = (await api.get("/erp/sales", { params: archiveParams })).data || [];
      const allTaxInvoices = (await api.get("/erp/tax-invoices", { params: archiveParams })).data || [];
      cancelledSales.value = allSales.filter((item) => item.invoice_status === "cancelled");
      cancelledTaxInvoices.value = allTaxInvoices.filter((item) => item.status === "cancelled");
    } else {
      cancelledSales.value = [];
      cancelledTaxInvoices.value = [];
    }
  } catch (error) {
    showNotice(errorMessage(error, "매출 정보를 불러오지 못했습니다."), "error");
  } finally {
    loadingSales.value = false;
  }
}
async function loadSaleSummary() {
  try {
    saleSummary.value = (await api.get("/erp/sales/summary", {
      params: {
        date_from: saleSummaryForm.date_from || undefined,
        date_to: saleSummaryForm.date_to || undefined,
        partner_id: saleSummaryForm.partner_id || undefined,
      },
    })).data;
  } catch (error) {
    showNotice(errorMessage(error, "매출 요약을 불러오지 못했습니다."), "error");
  }
}
async function loadReceivables() {
  try {
    receivableSummary.value = (await api.get("/erp/sales/receivables", {
      params: {
        date_from: saleSummaryForm.date_from || undefined,
        date_to: saleSummaryForm.date_to || undefined,
        partner_id: saleSummaryForm.partner_id || undefined,
      },
    })).data;
  } catch (error) {
    showNotice(errorMessage(error, "미수금 현황을 불러오지 못했습니다."), "error");
  }
}
async function loadAuditLogs() {
  loading.value = true;
  try {
    auditLogs.value = (await api.get("/erp/audit-logs", {
      params: {
        date_from: saleSummaryForm.date_from || undefined,
        date_to: saleSummaryForm.date_to || undefined,
        partner_id: saleSummaryForm.partner_id || undefined,
        limit: 300,
      },
    })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "감사 로그를 불러오지 못했습니다."), "error");
  } finally {
    loading.value = false;
  }
}
async function refreshSalesStatus() {
  includeCancelledSales.value = true;
  await Promise.all([
    loadSales(false),
    loadSaleSummary(),
    loadReceivables(),
    loadPayments(),
    loadExpenses(),
    loadJournals(),
    loadAuditLogs(),
  ]);
}
async function loadPayments() {
  loadingPayments.value = true;
  try {
    const params = { limit: 200 };
    if (paymentQuery.value) params.q = paymentQuery.value;
    if (activeTab.value === "salesStatus") {
      params.date_from = saleSummaryForm.date_from || undefined;
      params.date_to = saleSummaryForm.date_to || undefined;
      params.partner_id = saleSummaryForm.partner_id || undefined;
    }
    payments.value = (await api.get("/erp/payments", { params })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "입금 정보를 불러오지 못했습니다."), "error");
  } finally {
    loadingPayments.value = false;
  }
}
async function loadExpenses() {
  loadingExpenses.value = true;
  try {
    const params = { limit: 200 };
    if (expenseQuery.value) params.q = expenseQuery.value;
    if (activeTab.value === "salesStatus") {
      params.date_from = saleSummaryForm.date_from || undefined;
      params.date_to = saleSummaryForm.date_to || undefined;
      params.partner_id = saleSummaryForm.partner_id || undefined;
    }
    expenses.value = (await api.get("/erp/expenses", { params })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "비용 정보를 불러오지 못했습니다."), "error");
  } finally {
    loadingExpenses.value = false;
  }
}
async function loadAssets() {
  loadingAssets.value = true;
  try {
    const params = { limit: 200 };
    if (assetQuery.value) params.q = assetQuery.value;
    assets.value = (await api.get("/erp/assets", { params })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "자산 정보를 불러오지 못했습니다."), "error");
  } finally {
    loadingAssets.value = false;
  }
}
async function loadWorks() {
  loading.value = true;
  try {
    works.value = (await api.get("/erp/works", { params: { q: workQuery.value || undefined, limit: 500 } })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "작업을 불러오지 못했습니다."), "error");
  } finally {
    loading.value = false;
  }
}
async function loadEstimates() {
  try {
    estimates.value = (await api.get("/erp/estimates", {
      params: { q: estimateQuery.value || undefined, limit: 100 },
    })).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "견적서를 불러오지 못했습니다."), "error");
  }
}
async function selectTab(key) {
  activeTab.value = key;
  if (key === "works") await Promise.all([loadPartners(), loadWorks(), loadEstimates()]);
  if (key === "sales") {
    includeCancelledSales.value = false;
    await Promise.all([loadPartners(), loadWorks(), loadSales()]);
  }
  if (key === "salesStatus") await Promise.all([loadPartners(), loadWorks(), refreshSalesStatus()]);
  if (key === "payments") await Promise.all([loadPartners(), loadWorks(), loadSales(false), loadPayments()]);
  if (key === "expenses") await Promise.all([loadPartners(), loadAccounts(), loadWorks(), loadExpenses()]);
  if (key === "assets") await Promise.all([loadPartners(), loadAccounts(), loadWorks(), loadAssets()]);
  if (key === "journals") await Promise.all([loadPartners(), loadAccounts(), loadWorks(), loadJournals()]);
}
function resetWorkForm() {
  Object.assign(workForm, blankWork());
  workSubTab.value = "form";
}
function editWork(work) {
  selectedWork.value = work;
  Object.assign(workForm, blankWork(), work, {
    source_estimate_record_id: work.source_estimate_record_id || "",
  });
  if (work.estimate_no && !estimates.value.some((item) => item.id === work.source_estimate_record_id)) {
    estimateQuery.value = work.estimate_no;
    loadEstimates();
  }
  resetMailForm();
  loadDocuments(work.id);
  loadMailHistories(work.id);
  loadWorkDeleteBlockers(work.id);
  workSubTab.value = "documents";
  window.scrollTo({ top: 0, behavior: "smooth" });
}
function closeDocumentHub() {
  selectedWork.value = null;
  workDeleteBlockers.value = null;
  documents.value = [];
  mailHistories.value = [];
  clearThumbnailUrls();
  documentFilter.value = "";
  resetMailForm();
}
async function loadWorkDeleteBlockers(workId = selectedWork.value?.id) {
  if (!workId) return;
  try {
    workDeleteBlockers.value = (await api.get(`/erp/works/${workId}/delete-blockers`)).data;
  } catch (error) {
    workDeleteBlockers.value = null;
  }
}
async function saveWork() {
  saving.value = true;
  try {
    const payload = {
      partner_id: Number(workForm.partner_id),
      source_estimate_record_id: workForm.source_estimate_record_id ? Number(workForm.source_estimate_record_id) : null,
      worker_id: workForm.worker_id || null,
      work_date: workForm.work_date,
      status: workForm.status,
      title: workForm.title,
      memo: workForm.memo || null,
    };
    if (workForm.id) await api.put(`/erp/works/${workForm.id}`, payload);
    else await api.post("/erp/works", payload);
    showNotice(workForm.id ? "작업을 수정했습니다." : "새 작업번호를 생성했습니다.");
    resetWorkForm();
    await loadWorks();
  } catch (error) {
    showNotice(errorMessage(error, "작업 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function deleteWork() {
  if (!workForm.id) return;
  if (!window.confirm(`${workForm.work_no} 작업을 삭제할까요? 연결 자료가 있으면 삭제되지 않습니다.`)) return;
  saving.value = true;
  try {
    await api.delete(`/erp/works/${workForm.id}`);
    closeDocumentHub();
    resetWorkForm();
    await loadWorks();
    showNotice("작업을 삭제했습니다.");
  } catch (error) {
    if (workForm.id) await loadWorkDeleteBlockers(workForm.id);
    showNotice(errorMessage(error, "작업을 삭제하지 못했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function linkSelectedEstimatePartner() {
  if (!selectedEstimate.value) return;
  if (!workForm.partner_id) {
    showNotice("ERP 거래처를 먼저 선택해 주세요.", "error");
    return;
  }
  saving.value = true;
  try {
    const updated = (await api.put(`/erp/estimates/${selectedEstimate.value.id}/erp-partner`, {
      erp_partner_id: Number(workForm.partner_id),
      memo: "ERP 작업 화면에서 명시 매핑",
    })).data;
    estimates.value = estimates.value.map((item) => (String(item.id) === String(updated.id) ? updated : item));
    showNotice(`견적서 ${updated.no}를 ERP 거래처 ${updated.erp_partner_name || ""}에 매핑했습니다.`);
  } catch (error) {
    showNotice(errorMessage(error, "견적서 ERP 거래처 매핑에 실패했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function createWorkFromSelectedEstimate() {
  if (!selectedEstimate.value) return;
  const partnerId = workForm.partner_id || selectedEstimate.value.erp_partner_id;
  if (!partnerId) {
    showNotice("ERP 거래처를 먼저 선택하거나 매핑해 주세요. 견적 수신 텍스트만으로 거래처를 자동 등록하지 않습니다.", "error");
    return;
  }
  saving.value = true;
  try {
    const created = (await api.post(`/erp/estimates/${selectedEstimate.value.id}/create-work`, {
      partner_id: Number(partnerId),
      work_date: selectedEstimate.value.estimate_date || localDate(),
      status: "planned",
      title: null,
      memo: null,
    })).data;
    await Promise.all([loadPartners(), loadWorks(), loadEstimates()]);
    editWork(created);
    showNotice(`${created.work_no} 작업을 견적서에서 생성/연결했습니다.`);
  } catch (error) {
    showNotice(errorMessage(error, "견적서에서 ERP 작업을 생성하지 못했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function createSaleFromSelectedWorkEstimate() {
  const sale = await createSaleFromSelectedWorkEstimateData();
  if (sale) showNotice(`${sale.item_name} 매출을 견적서에서 생성/연결했습니다.`);
}
async function createSaleFromSelectedWorkEstimateData() {
  if (!selectedWork.value) return;
  saving.value = true;
  try {
    const sale = (await api.post(`/erp/works/${selectedWork.value.id}/create-sale-from-estimate`, {
      sale_date: selectedWork.value.work_date || localDate(),
      item_name: null,
      memo: null,
    })).data;
    await Promise.all([loadSales(false), loadWorks(), loadSaleSummary(), loadReceivables()]);
    const updatedWork = works.value.find((item) => String(item.id) === String(selectedWork.value.id));
    if (updatedWork) selectedWork.value = updatedWork;
    return sale;
  } catch (error) {
    showNotice(errorMessage(error, "견적서에서 매출을 생성하지 못했습니다."), "error");
    return null;
  } finally {
    saving.value = false;
  }
}
async function issueInvoiceFromSelectedWorkEstimate() {
  const sale = await createSaleFromSelectedWorkEstimateData();
  if (!sale) return;
  if (sale.invoice_status === "issued" || sale.tax_invoice_id) {
    showNotice("이미 세금계산서가 발행된 매출입니다.");
    return;
  }
  await issueTaxInvoicePrompt(sale);
}
function resetSaleForm() {
  Object.assign(saleForm, blankSale());
  saleSubTab.value = "form";
}
function editSale(sale) {
  Object.assign(saleForm, blankSale(), sale, {
    work_id: sale.work_id || "",
    partner_id: sale.partner_id || "",
  });
  saleSubTab.value = "form";
  window.scrollTo({ top: 0, behavior: "smooth" });
}
function syncSaleWork() {
  const work = works.value.find((item) => String(item.id) === String(saleForm.work_id));
  if (work) saleForm.partner_id = work.partner_id;
}
function saleInvoiceStatusLabel(status) {
  return { draft: "미발행", issued: "발행됨", cancelled: "취소" }[status] || status;
}
function taxInvoiceStatusLabel(status) {
  return { draft: "작성", issued: "발행", cancelled: "취소" }[status] || status;
}
function taxInvoiceApiStatusLabel(status) {
  return {
    pending: "API 대기",
    sent: "전송됨",
    issued: "외부 발행완료",
    cancel_requested: "취소 요청",
    cancelled: "외부 취소완료",
    failed: "API 실패",
  }[status] || status || "API 대기";
}
function paymentStatusLabel(status) {
  return { posted: "정상", cancelled: "취소" }[status] || status || "정상";
}
function auditActionLabel(action) {
  return {
    create: "생성",
    update: "수정",
    delete: "삭제",
    deactivate: "비활성",
    restore: "복구",
    cancel: "취소",
    issue: "발행",
    send: "발송",
    upload: "업로드",
    depreciate: "감가상각",
    create_from_estimate: "견적기반 생성",
    link_estimate_partner: "견적-거래처 연결",
    link_work_estimate: "작업-견적 연결",
    update_api_status: "API 상태수정",
  }[action] || action;
}
function auditEntityLabel(entityType) {
  return {
    partner: "거래처",
    estimate: "견적",
    work: "작업",
    document: "문서",
    mail_history: "메일",
    journal: "전표",
    sale: "매출",
    tax_invoice: "세금계산서",
    payment: "입금",
    expense: "비용",
    asset: "자산",
  }[entityType] || entityType;
}
function expensePaymentMethodLabel(method) {
  return {
    business_account: "사업용 통장",
    business_card: "사업용 카드",
    personal_money: "개인 돈",
    cash: "현금",
  }[method] || method;
}
function expenseStatusLabel(status) {
  return { posted: "정상", cancelled: "취소" }[status] || status || "정상";
}
async function saveSale() {
  saving.value = true;
  const isEdit = Boolean(saleForm.id);
  try {
    const payload = {
      work_id: saleForm.work_id || null,
      partner_id: Number(saleForm.partner_id),
      sale_date: saleForm.sale_date,
      item_name: saleForm.item_name,
      quantity: Number(saleForm.quantity || 0),
      unit_price: Number(saleForm.unit_price || 0),
      invoice_status: saleForm.invoice_status,
      memo: saleForm.memo || null,
    };
    if (saleForm.id) await api.put(`/erp/sales/${saleForm.id}`, payload);
    else await api.post("/erp/sales", payload);
    resetSaleForm();
    await Promise.all([loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    showNotice(isEdit ? "매출을 수정했습니다." : "매출을 저장했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "매출 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function issueTaxInvoicePrompt(sale) {
  const defaultDate = localDate();
  const invoiceNumber = window.prompt(`${sale.item_name} 세금계산서 번호`, "");
  if (invoiceNumber === null) return;
  const issueDate = window.prompt(`${sale.item_name} 발행일 (YYYY-MM-DD)`, defaultDate);
  if (issueDate === null) return;
  try {
    await api.post(`/erp/sales/${sale.id}/issue-tax-invoice`, {
      issue_date: issueDate || defaultDate,
      invoice_number: invoiceNumber || null,
      api_status: "pending",
      memo: null,
    });
    await Promise.all([loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    if (selectedWork.value) {
      const updatedWork = works.value.find((item) => String(item.id) === String(selectedWork.value.id));
      if (updatedWork) selectedWork.value = updatedWork;
      await loadWorkDeleteBlockers();
    }
    showNotice("세금계산서를 발행했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "세금계산서 발행 중 오류가 발생했습니다."), "error");
  }
}
async function cancelTaxInvoicePrompt(invoice) {
  const defaultDate = localDate();
  const reason = window.prompt(`${invoice.invoice_number} 세금계산서 취소 사유`, "");
  if (reason === null) return;
  if (!reason.trim()) {
    showNotice("세금계산서 취소 사유를 입력해 주세요.", "error");
    return;
  }
  const cancelDate = window.prompt(`${invoice.invoice_number} 취소일 (YYYY-MM-DD)`, defaultDate);
  if (cancelDate === null) return;
  if (!window.confirm("취소전표를 자동 생성하고 매출을 미발행 상태로 되돌립니다. 진행할까요?")) return;
  try {
    await api.post(`/erp/tax-invoices/${invoice.id}/cancel`, {
      cancel_date: cancelDate || defaultDate,
      reason,
      api_status: "cancel_requested",
    });
    await Promise.all([loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    if (selectedWork.value) await loadWorkDeleteBlockers();
    showNotice("세금계산서를 취소했습니다. 필요하면 같은 매출에서 다시 발행할 수 있습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "세금계산서 취소 중 오류가 발생했습니다."), "error");
  }
}
async function cancelSalePrompt(sale) {
  const defaultDate = localDate();
  const reason = window.prompt(`${sale.item_name} 매출 취소 사유`, "");
  if (reason === null) return;
  if (!reason.trim()) {
    showNotice("매출 취소 사유를 입력해 주세요.", "error");
    return;
  }
  const cancelDate = window.prompt("매출 취소일 (YYYY-MM-DD)", defaultDate);
  if (cancelDate === null) return;
  if (!window.confirm("매출을 취소 상태로 기록하고 작업 연결을 정리합니다. 진행할까요?")) return;
  try {
    await api.post(`/erp/sales/${sale.id}/cancel`, {
      cancel_date: cancelDate || defaultDate,
      reason,
    });
    await Promise.all([loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    if (selectedWork.value) {
      const updatedWork = works.value.find((item) => String(item.id) === String(selectedWork.value.id));
      if (updatedWork) selectedWork.value = updatedWork;
      await loadWorkDeleteBlockers();
    }
    showNotice("매출을 취소했습니다. 작업 삭제 전 입금/문서/메일 등 남은 연결도 확인하세요.");
  } catch (error) {
    showNotice(errorMessage(error, "매출 취소 중 오류가 발생했습니다."), "error");
  }
}
async function deleteSale(sale) {
  if (!window.confirm(`${sale.item_name} 매출을 삭제할까요?`)) return;
  try {
    await api.delete(`/erp/sales/${sale.id}`);
    await Promise.all([loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    if (selectedWork.value) await loadWorkDeleteBlockers();
    showNotice("매출을 삭제했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "매출을 삭제하지 못했습니다."), "error");
  }
}
async function viewEstimateById(estimateId) {
  const win = window.open("", "_blank");
  if (!win) {
    showNotice("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해 주세요.", "error");
    return;
  }
  win.document.write("<!doctype html><title>견적서 불러오는 중</title><p>견적서를 불러오고 있습니다.</p>");
  try {
    const detail = await api.get(`/estimate/records/${estimateId}`);
    const payload = detail.data?.payload;
    if (!payload) throw new Error("estimate payload missing");
    const response = await api.post("/estimate/render", payload, {
      params: { save: false },
      responseType: "text",
      headers: { "Content-Type": "application/json" },
    });
    const blob = new Blob([response.data], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    win.location.replace(url);
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    win.close();
    showNotice(errorMessage(error, "견적서를 열지 못했습니다."), "error");
  }
}
async function loadDocuments(workId = selectedWork.value?.id) {
  if (!workId) return;
  loadingDocuments.value = true;
  try {
    documents.value = (await api.get(`/erp/works/${workId}/documents`)).data || [];
    await loadThumbnailUrls();
  } catch (error) {
    showNotice(errorMessage(error, "문서를 불러오지 못했습니다."), "error");
  } finally {
    loadingDocuments.value = false;
  }
}
function clearThumbnailUrls() {
  Object.values(thumbnailUrls.value).forEach((url) => URL.revokeObjectURL(url));
  thumbnailUrls.value = {};
}
async function loadThumbnailUrls() {
  clearThumbnailUrls();
  const nextUrls = {};
  await Promise.all(
    documents.value
      .filter((item) => item.document_type === "work_photo" && item.thumbnail_path)
      .map(async (item) => {
        try {
          const response = await api.get(`/erp/documents/${item.id}/thumbnail`, { responseType: "blob" });
          nextUrls[item.id] = URL.createObjectURL(response.data);
        } catch (error) {
          nextUrls[item.id] = "";
        }
      })
  );
  thumbnailUrls.value = nextUrls;
}
async function loadMailHistories(workId = selectedWork.value?.id) {
  if (!workId) return;
  loadingMailHistories.value = true;
  try {
    mailHistories.value = (await api.get(`/erp/works/${workId}/mail-histories`)).data || [];
  } catch (error) {
    showNotice(errorMessage(error, "메일 이력을 불러오지 못했습니다."), "error");
  } finally {
    loadingMailHistories.value = false;
  }
}
function resetMailForm() {
  const partner = partners.value.find((item) => String(item.id) === String(selectedWork.value?.partner_id));
  mailForm.recipient = partner?.email || "";
  mailForm.cc = "";
  mailForm.subject = selectedWork.value ? `[${selectedWork.value.work_no}] ${selectedWork.value.title}` : "";
  mailForm.body = "";
}
async function saveMailHistory(sendNow = false) {
  if (!selectedWork.value) return;
  saving.value = true;
  try {
    await api.post(`/erp/works/${selectedWork.value.id}/mail-histories`, {
      recipient: mailForm.recipient,
      cc: mailForm.cc || null,
      subject: mailForm.subject,
      body: mailForm.body || null,
      send_now: sendNow,
    });
    await Promise.all([loadMailHistories(), loadWorks()]);
    showNotice(sendNow ? "메일 발송 요청을 처리했습니다." : "메일 이력을 저장했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "메일 처리 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function sendMailHistory(mail) {
  saving.value = true;
  try {
    await api.post(`/erp/mail-histories/${mail.id}/send`);
    await Promise.all([loadMailHistories(), loadWorks()]);
    showNotice("메일 재발송 요청을 처리했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "메일 재발송 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
function resetPaymentForm() {
  Object.assign(paymentForm, blankPayment());
}
function resetExpenseForm() {
  Object.assign(expenseForm, blankExpense());
}
function resetAssetForm() {
  Object.assign(assetForm, blankAsset());
}
function syncPaymentWork() {
  const work = works.value.find((item) => String(item.id) === String(paymentForm.work_id));
  if (work) paymentForm.partner_id = work.partner_id;
}
async function savePayment() {
  saving.value = true;
  try {
    const payload = {
      work_id: paymentForm.work_id || null,
      partner_id: Number(paymentForm.partner_id),
      payment_date: paymentForm.payment_date,
      amount: Number(paymentForm.amount || 0),
      bank_account: paymentForm.bank_account || null,
      related_sale_id: paymentForm.related_sale_id || null,
      memo: paymentForm.memo || null,
      allocations: paymentForm.related_sale_id
        ? undefined
        : [],
    };
    await api.post("/erp/payments", payload);
    resetPaymentForm();
    await Promise.all([loadPayments(), loadWorks()]);
    showNotice("입금을 저장했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "입금 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function cancelPaymentPrompt(payment) {
  const defaultDate = localDate();
  const reason = window.prompt(`${payment.partner_name} 입금 취소 사유`, "");
  if (reason === null) return;
  if (!reason.trim()) {
    showNotice("입금 취소 사유를 입력해 주세요.", "error");
    return;
  }
  const cancelDate = window.prompt("입금 취소일 (YYYY-MM-DD)", defaultDate);
  if (cancelDate === null) return;
  if (!window.confirm("입금 취소전표를 자동 생성합니다. 진행할까요?")) return;
  try {
    await api.post(`/erp/payments/${payment.id}/cancel`, {
      cancel_date: cancelDate || defaultDate,
      reason,
    });
    await Promise.all([loadPayments(), loadSales(), loadWorks(), loadSaleSummary(), loadReceivables()]);
    if (selectedWork.value) await loadWorkDeleteBlockers();
    showNotice("입금을 취소했습니다. 정정이 필요한 경우 새 입금으로 다시 등록하세요.");
  } catch (error) {
    showNotice(errorMessage(error, "입금 취소 중 오류가 발생했습니다."), "error");
  }
}
async function saveExpense() {
  saving.value = true;
  try {
    await api.post("/erp/expenses", {
      work_id: expenseForm.work_id || null,
      partner_id: expenseForm.partner_id || null,
      expense_date: expenseForm.expense_date,
      account_id: Number(expenseForm.account_id),
      item_name: expenseForm.item_name,
      supply_amount: Number(expenseForm.supply_amount || 0),
      vat_amount: Number(expenseForm.vat_amount || 0),
      total_amount: expensePreviewTotal.value,
      payment_method: expenseForm.payment_method,
      paid_by: expenseForm.payment_method,
      memo: expenseForm.memo || null,
    });
    resetExpenseForm();
    await Promise.all([loadExpenses(), loadWorks()]);
    showNotice("비용을 저장하고 전표를 자동 생성했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "비용 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function cancelExpensePrompt(expense) {
  const defaultDate = localDate();
  const reason = window.prompt(`${expense.item_name} 비용 취소 사유`, "");
  if (reason === null) return;
  if (!reason.trim()) {
    showNotice("비용 취소 사유를 입력해 주세요.", "error");
    return;
  }
  const cancelDate = window.prompt("비용 취소일 (YYYY-MM-DD)", defaultDate);
  if (cancelDate === null) return;
  if (!window.confirm("비용 취소전표를 자동 생성합니다. 진행할까요?")) return;
  try {
    await api.post(`/erp/expenses/${expense.id}/cancel`, {
      cancel_date: cancelDate || defaultDate,
      reason,
    });
    await Promise.all([loadExpenses(), loadWorks()]);
    if (selectedWork.value) await loadWorkDeleteBlockers();
    showNotice("비용을 취소했습니다. 정정이 필요한 경우 새 비용으로 다시 등록하세요.");
  } catch (error) {
    showNotice(errorMessage(error, "비용 취소 중 오류가 발생했습니다."), "error");
  }
}
async function saveAsset() {
  saving.value = true;
  try {
    await api.post("/erp/assets", {
      work_id: assetForm.work_id || null,
      partner_id: assetForm.partner_id || null,
      asset_account_id: Number(assetForm.asset_account_id),
      asset_name: assetForm.asset_name,
      category: assetForm.category,
      purchase_date: assetForm.purchase_date,
      purchase_amount: Number(assetForm.purchase_amount || 0),
      useful_life_years: Number(assetForm.useful_life_years || 5),
      depreciation_method: "straight_line",
      acquisition_method: assetForm.acquisition_method,
      memo: assetForm.memo || null,
    });
    resetAssetForm();
    await Promise.all([loadAssets(), loadWorks()]);
    showNotice("자산을 저장하고 취득 전표를 자동 생성했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "자산 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function depreciateAssetPrompt(asset) {
  const defaultDate = localDate();
  const defaultAmount = Math.min(
    Number(asset.current_value || 0),
    Number(asset.purchase_amount || 0) / Math.max(Number(asset.useful_life_years || 1), 1)
  );
  const amount = window.prompt(`${asset.asset_name} 감가상각 금액`, String(Math.round(defaultAmount * 100) / 100));
  if (amount === null) return;
  const parsedAmount = Number(amount || 0);
  if (!parsedAmount || parsedAmount <= 0) {
    showNotice("감가상각 금액은 0보다 커야 합니다.", "error");
    return;
  }
  const depreciationDate = window.prompt("감가상각일 (YYYY-MM-DD)", defaultDate);
  if (depreciationDate === null) return;
  try {
    await api.post(`/erp/assets/${asset.id}/depreciations`, {
      depreciation_date: depreciationDate || defaultDate,
      amount: parsedAmount,
      memo: null,
    });
    await loadAssets();
    showNotice("감가상각 전표를 생성했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "감가상각 처리 중 오류가 발생했습니다."), "error");
  }
}
function selectFiles(event) {
  uploadFiles.value = Array.from(event.target.files || []).slice(0, 10);
}
async function uploadDocuments() {
  if (!selectedWork.value || !uploadFiles.value.length) return;
  uploading.value = true;
  uploadProgress.value = "";
  const formData = new FormData();
  uploadFiles.value.forEach((file) => formData.append("files", file));
  try {
    await api.post(`/erp/works/${selectedWork.value.id}/documents`, formData, {
      params: {
        document_type: uploadForm.document_type,
        title: uploadForm.title || undefined,
      },
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress(event) {
        if (event.total) uploadProgress.value = `${Math.round((event.loaded / event.total) * 100)}%`;
      },
    });
    uploadFiles.value = [];
    uploadForm.title = "";
    fileInputKey.value += 1;
    await Promise.all([loadDocuments(), loadWorks()]);
    selectedWork.value = works.value.find((item) => item.id === selectedWork.value?.id) || selectedWork.value;
    showNotice("문서를 NAS에 저장했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "문서 업로드 중 오류가 발생했습니다."), "error");
  } finally {
    uploading.value = false;
    uploadProgress.value = "";
  }
}
async function downloadDocument(document) {
  try {
    const response = await api.get(`/erp/documents/${document.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = document.file_name || "document";
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    showNotice(errorMessage(error, "문서를 다운로드하지 못했습니다."), "error");
  }
}
async function deleteDocument(document) {
  if (!window.confirm(`${document.file_name} 문서를 삭제할까요? NAS 파일도 함께 삭제됩니다.`)) return;
  try {
    await api.delete(`/erp/documents/${document.id}`);
    await Promise.all([loadDocuments(), loadWorks()]);
    if (selectedWork.value) await loadWorkDeleteBlockers();
    showNotice("문서를 삭제했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "문서를 삭제하지 못했습니다."), "error");
  }
}
function resetPartnerForm() {
  Object.assign(partnerForm, blankPartner());
  partnerSubTab.value = "form";
}
function editPartner(partner) {
  Object.assign(partnerForm, blankPartner(), partner);
  partnerSubTab.value = "form";
  window.scrollTo({ top: 0, behavior: "smooth" });
}
async function savePartner() {
  saving.value = true;
  try {
    const payload = { ...partnerForm };
    delete payload.id;
    if (partnerForm.id) await api.put(`/erp/partners/${partnerForm.id}`, payload);
    else await api.post("/erp/partners", payload);
    showNotice(partnerForm.id ? "거래처를 수정했습니다." : "거래처를 등록했습니다.");
    resetPartnerForm();
    await Promise.all([loadPartners(), loadHealth()]);
  } catch (error) {
    showNotice(errorMessage(error, "거래처 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
async function hidePartner(partner) {
  if (!window.confirm(`${partner.name} 거래처를 목록에서 숨길까요?`)) return;
  try {
    await api.delete(`/erp/partners/${partner.id}`);
    if (partnerForm.id === partner.id) resetPartnerForm();
    await Promise.all([loadPartners(), loadHealth()]);
    showNotice("거래처를 숨겼습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "거래처를 숨기지 못했습니다."), "error");
  }
}
function addLine() {
  journalForm.lines.push(blankLine());
}
function removeLine(index) {
  if (journalForm.lines.length > 2) journalForm.lines.splice(index, 1);
}
function clearOpposite(line, side) {
  if (Number(line[side] || 0) > 0) line[side === "debit" ? "credit" : "debit"] = "";
}
function syncJournalWork() {
  const work = works.value.find((item) => String(item.id) === String(journalForm.work_id));
  if (work) journalForm.partner_id = work.partner_id;
}
function resetJournalForm() {
  Object.assign(journalForm, blankJournal());
}
async function saveJournal() {
  saving.value = true;
  try {
    const payload = {
      entry_date: journalForm.entry_date,
      description: journalForm.description,
      partner_id: journalForm.partner_id || null,
      work_id: journalForm.work_id || null,
      source_type: "manual",
      source_id: null,
      lines: journalForm.lines.map((line) => ({
        account_id: Number(line.account_id),
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
        memo: line.memo || null,
      })),
    };
    await api.post("/erp/journals", payload);
    resetJournalForm();
    await loadJournals();
    showNotice("전표를 저장했습니다.");
  } catch (error) {
    showNotice(errorMessage(error, "전표 저장 중 오류가 발생했습니다."), "error");
  } finally {
    saving.value = false;
  }
}
function money(value) {
  return Number(value || 0).toLocaleString("ko-KR", { maximumFractionDigits: 2 });
}
function accountTypeLabel(type) {
  return accountTypes.find((item) => item.value === type)?.label || type;
}
function workStatusLabel(status) {
  return { planned: "예정", in_progress: "진행 중", completed: "완료", cancelled: "취소" }[status] || status;
}
function documentCount(type) {
  return type ? documents.value.filter((item) => item.document_type === type).length : documents.value.length;
}
function documentTypeLabel(type) {
  return documentTypes.find((item) => item.value === type)?.label || type;
}
function documentIcon(type) {
  return documentTypes.find((item) => item.value === type)?.icon || "문";
}
function mailStatusLabel(status) {
  return { pending: "대기", sent: "발송됨", failed: "실패" }[status] || status;
}
function fileSize(bytes) {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)}MB`;
  if (value >= 1024) return `${Math.round(value / 1024)}KB`;
  return `${value}B`;
}
function compactDate(value) {
  return String(value || "").replace("T", " ").slice(0, 16);
}
</script>

<style scoped>
.erp-page { display: grid; gap: 22px; }
.erp-hero { display:flex; justify-content:space-between; gap:28px; align-items:flex-end; padding:30px; color:#fff; background:linear-gradient(135deg,#123e36,#1b7f6c); border-radius:20px; box-shadow:0 18px 40px rgba(15,91,79,.22); }
.eyebrow { font-size:12px; letter-spacing:.16em; opacity:.7; font-weight:700; }
.erp-hero h1 { margin:8px 0; font-size:clamp(1.8rem,4vw,2.8rem); }
.erp-hero p { margin:0; opacity:.78; }
.hero-status { flex:none; padding:10px 14px; border:1px solid rgba(255,255,255,.24); border-radius:999px; font-size:13px; background:rgba(255,255,255,.08); }
.status-dot { display:inline-block; width:8px; height:8px; margin-right:7px; border-radius:50%; background:#e9a23b; }
.status-dot.ready { background:#7ce8ab; }
.erp-tabs { display:flex; align-items:center; gap:8px; padding:7px; background:#fff; border:1px solid var(--border); border-radius:14px; overflow-x:auto; }
.erp-tabs button,.estimate-link { white-space:nowrap; border:0; background:transparent; padding:10px 16px; border-radius:9px; color:var(--muted); font-weight:700; cursor:pointer; text-decoration:none; }
.erp-tabs button.active { background:#e9f4f1; color:var(--accent-strong); }
.estimate-link { margin-left:auto; color:var(--accent); }
.sub-tabs { display:flex; flex-wrap:wrap; gap:8px; padding:9px; border:1px solid var(--border); border-radius:14px; background:#fff; }
.sub-tabs button { padding:9px 13px; border:1px solid var(--border); border-radius:999px; color:var(--muted); background:#fff; cursor:pointer; font-weight:800; }
.sub-tabs button.active { color:#fff; border-color:var(--accent); background:var(--accent); }
.notice { position:sticky; top:95px; z-index:5; margin:0; padding:13px 16px; border-radius:10px; background:#e5f5ed; color:#17643f; border:1px solid #b9dfca; }
.notice.error { background:#fff0ee; color:#9d3025; border-color:#efc1bb; }
.split-layout,.journal-layout { display:grid; grid-template-columns:minmax(0,1fr) minmax(340px,.9fr); gap:20px; align-items:start; }
.split-layout.single,.journal-layout.single { grid-template-columns:1fr; }
.panel { padding:24px; background:#fff; border:1px solid var(--border); border-radius:18px; box-shadow:0 10px 30px rgba(31,42,36,.06); }
.panel-heading { display:flex; justify-content:space-between; align-items:center; gap:16px; margin-bottom:22px; }
.panel-heading>div { display:flex; align-items:center; gap:10px; }
.panel-heading h2 { margin:0; font-size:1.25rem; }
.section-number { display:grid; place-items:center; width:30px; height:30px; color:var(--accent); background:#e9f4f1; border-radius:8px; font-size:11px; font-weight:800; }
.count { color:var(--muted); font-size:13px; }
.text-button { border:0; background:none; color:var(--accent); font-weight:700; cursor:pointer; }
.erp-form,.journal-basics { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:15px; }
.erp-form label,.journal-form label { display:grid; gap:7px; }
.erp-form label span,.journal-form label span { font-size:12px; font-weight:700; color:var(--muted); }
.wide { grid-column:1/-1; }
input,select,textarea { width:100%; padding:11px 12px; color:var(--ink); background:#fbfcfb; border:1px solid var(--border); border-radius:9px; font:inherit; }
input:focus,select:focus,textarea:focus { outline:2px solid rgba(27,127,108,.14); border-color:var(--accent); }
.form-actions { display:flex; justify-content:flex-end; gap:9px; margin-top:5px; }
.button { padding:10px 16px; border-radius:9px; border:1px solid var(--border); font-weight:700; cursor:pointer; }
.button.primary { color:#fff; background:var(--accent); border-color:var(--accent); }
.button.ghost { color:var(--ink); background:#fff; }
.button.danger { color:#a33b31; border-color:#f0c4bd; background:#fff7f5; }
.button:disabled { opacity:.45; cursor:not-allowed; }
.search-row { display:flex; gap:8px; margin-bottom:14px; }
.inline-check { display:flex; align-items:center; gap:6px; padding:0 10px; color:var(--muted); font-size:13px; font-weight:800; white-space:nowrap; }
.inline-check input { width:auto; }
.data-list,.journal-list { display:grid; gap:9px; max-height:610px; overflow:auto; }
.table-scroll { max-height:610px; overflow:auto; border:1px solid var(--border); border-radius:12px; background:#fff; }
.erp-table { width:100%; min-width:900px; border-collapse:collapse; font-size:12px; }
.erp-table th,.erp-table td { padding:10px 11px; border-bottom:1px solid #edf2ef; text-align:left; vertical-align:middle; }
.erp-table th { position:sticky; top:0; z-index:1; color:var(--muted); background:#f7faf8; font-weight:900; }
.erp-table tr:hover td { background:#fbfdfc; }
.erp-table .num { text-align:right; white-space:nowrap; }
.erp-table .strong { color:var(--accent-strong); font-weight:900; }
.table-actions { display:flex; flex-wrap:wrap; gap:5px; }
.mini-button { padding:6px 8px; border:1px solid var(--border); border-radius:7px; color:var(--ink); background:#fff; cursor:pointer; font-size:11px; font-weight:800; }
.mini-button.primary { color:#fff; border-color:var(--accent); background:var(--accent); }
.mini-button.danger { color:#a33b31; border-color:#f0c4bd; background:#fff7f5; }
.cancel-archive { margin-top:18px; }
.muted-table td { color:#5f6f68; background:#fffdfb; }
.partner-row { display:flex; gap:8px; align-items:center; padding:4px; border:1px solid var(--border); border-radius:11px; }
.row-main { display:grid; gap:3px; flex:1; padding:10px; border:0; text-align:left; background:transparent; cursor:pointer; }
.row-main span,.row-main small { color:var(--muted); }
.icon-danger,.line-remove { width:32px; height:32px; border:0; border-radius:8px; color:#a33b31; background:#fff0ee; font-size:18px; cursor:pointer; }
.empty { padding:30px; text-align:center; color:var(--muted); }
.account-filters { display:flex; flex-wrap:wrap; gap:7px; margin-bottom:20px; }
.account-filters button { padding:8px 13px; border:1px solid var(--border); border-radius:999px; background:#fff; cursor:pointer; }
.account-filters button.active { color:#fff; background:var(--accent); border-color:var(--accent); }
.account-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:10px; }
.account-card { display:grid; gap:5px; padding:15px; border:1px solid var(--border); border-radius:11px; background:#fbfcfb; }
.account-card>span { color:var(--accent); font-size:12px; font-weight:800; }
.account-card small { color:var(--muted); }
.journal-layout { grid-template-columns:minmax(520px,1.2fr) minmax(320px,.8fr); }
.journal-form { display:grid; gap:18px; }
.journal-lines { display:grid; gap:7px; overflow-x:auto; }
.line-head,.line-row { display:grid; grid-template-columns:minmax(170px,1.3fr) 110px 110px minmax(120px,1fr) 34px; gap:7px; min-width:670px; align-items:center; }
.line-head { padding:0 4px; color:var(--muted); font-size:11px; font-weight:700; }
.add-line { justify-self:start; }
.balance-box { display:grid; grid-template-columns:1fr 1fr; gap:10px; padding:16px; border-radius:11px; background:#fff7ed; border:1px solid #f0d8b9; }
.balance-box>div { display:flex; justify-content:space-between; }
.balance-box p { grid-column:1/-1; margin:0; color:#9b6521; font-size:12px; }
.balance-box.balanced { background:#eaf6ef; border-color:#bfdfcd; }
.balance-box.balanced p { color:#237149; }
.journal-card { padding:15px; border:1px solid var(--border); border-radius:12px; }
.journal-card header { display:flex; justify-content:space-between; gap:12px; }
.journal-card header>div { display:grid; gap:3px; }
.journal-card header span,.journal-card header small { color:var(--muted); font-size:12px; }
.journal-card header b { color:var(--accent-strong); white-space:nowrap; }
.journal-detail { display:grid; gap:5px; margin-top:12px; padding-top:10px; border-top:1px dashed var(--border); }
.journal-detail div { display:flex; justify-content:space-between; gap:10px; font-size:12px; color:var(--muted); }
.journal-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:12px; }
.work-list { max-height:650px; }
.work-card { padding:16px; border:1px solid var(--border); border-radius:13px; cursor:pointer; transition:border-color .2s,transform .2s; }
.work-card:hover { border-color:var(--accent); transform:translateY(-1px); }
.work-card header { display:flex; justify-content:space-between; align-items:center; gap:12px; }
.work-card header>div { display:flex; align-items:center; gap:8px; }
.work-card time { color:var(--muted); font-size:12px; }
.work-card h3 { margin:12px 0 5px; font-size:1rem; }
.work-card p { margin:0; color:var(--muted); font-size:13px; }
.work-status { padding:4px 7px; border-radius:999px; color:#805719; background:#fff2d8; font-size:10px; font-weight:800; }
.work-status.in_progress { color:#175e84; background:#e4f4fc; }
.work-status.completed { color:#237149; background:#e5f5ec; }
.work-status.cancelled { color:#8f3c34; background:#fff0ee; }
.work-links { display:flex; flex-wrap:wrap; gap:6px; margin-top:13px; }
.work-links span { padding:5px 8px; border-radius:7px; color:var(--muted); background:#f4f6f5; font-size:11px; }
.work-links span.linked { color:var(--accent-strong); background:#e9f4f1; }
.estimate-picker { display:grid; gap:7px; }
.estimate-picker>span { color:var(--muted); font-size:12px; font-weight:700; }
.estimate-search { display:flex; gap:8px; }
.selected-estimate { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 12px; border-radius:9px; background:#e9f4f1; }
.selected-estimate>div { display:grid; gap:2px; }
.selected-estimate span { color:var(--muted); font-size:12px; }
.statement-state { color:#9a6a00 !important; font-weight:800; }
.statement-state.issued { color:var(--accent-strong) !important; }
.mapping-state { color:#b45309 !important; font-weight:800; }
.mapping-state.linked { color:var(--accent-strong) !important; }
.estimate-actions { display:flex !important; align-items:center; justify-content:flex-end; gap:8px; }
.heading-actions { display:flex; align-items:center; gap:10px; }
.sale-layout,.payment-layout { grid-template-columns:minmax(0,1fr) minmax(420px,.95fr); }
.sale-preview { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; padding:14px; border:1px solid #cfe4db; border-radius:12px; background:#f3faf7; }
.sale-preview div { display:grid; gap:2px; padding:12px; border-radius:10px; background:#fff; border:1px solid #e1eee7; }
.sale-preview span { color:var(--muted); font-size:12px; font-weight:700; }
.sale-preview strong { color:var(--accent-strong); }
.sale-preview p { grid-column:1/-1; margin:0; color:var(--muted); font-size:12px; }
.summary-controls { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)) auto; gap:10px; align-items:end; margin-bottom:12px; }
.summary-controls label { display:grid; gap:6px; }
.summary-controls label>span { color:var(--muted); font-size:12px; font-weight:800; }
.summary-box { display:grid; gap:12px; padding:14px; margin-bottom:20px; border:1px solid #cfe4db; border-radius:14px; background:#f7fbfa; }
.summary-box.board { margin-bottom:0; }
.summary-total { display:grid; gap:3px; }
.summary-total span,.summary-total small { color:var(--muted); }
.summary-total strong { color:var(--accent-strong); font-size:28px; }
.summary-columns { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
.summary-columns>div { min-width:0; padding:12px; border:1px solid var(--border); border-radius:12px; background:#fff; }
.summary-columns h3 { margin:0 0 8px; font-size:14px; }
.summary-columns p { display:flex; align-items:center; justify-content:space-between; gap:10px; margin:0; padding:6px 0; border-top:1px dashed #e3ebe8; font-size:12px; }
.summary-columns p:first-of-type { border-top:0; }
.summary-columns p span { overflow:hidden; white-space:nowrap; text-overflow:ellipsis; color:var(--muted); }
.summary-columns p strong { white-space:nowrap; color:var(--ink); }
.empty.mini { display:block !important; padding:8px 0 !important; text-align:left !important; border-top:0 !important; }
.invoice-heading { margin-top:18px; margin-bottom:12px; }
.status-panel { display:grid; gap:14px; }
.status-table { max-height:420px; }
.document-hub { display:grid; gap:20px; border-top:4px solid var(--accent); }
.document-heading { margin-bottom:0; }
.document-heading h2 { margin-bottom:3px; }
.document-heading p { margin:0; color:var(--muted); font-size:13px; }
.document-types { display:grid; grid-template-columns:repeat(auto-fit,minmax(105px,1fr)); gap:8px; }
.document-types button { display:grid; justify-items:start; gap:4px; padding:13px; border:1px solid var(--border); border-radius:11px; background:#fff; cursor:pointer; }
.document-types button>span { display:grid; place-items:center; width:28px; height:28px; border-radius:8px; color:var(--accent); background:#e9f4f1; font-weight:800; }
.document-types button small { color:var(--muted); }
.document-types button.active { border-color:var(--accent); background:#f1f8f6; }
.delete-status { display:flex; align-items:center; flex-wrap:wrap; gap:10px; padding:11px 13px; border:1px solid #fed7aa; border-radius:11px; color:#9a3412; background:#fff7ed; }
.delete-status.ready { color:var(--accent-strong); border-color:#b9dfca; background:#e9f4f1; }
.delete-status strong,.delete-status span { color:inherit; font-size:13px; }
.delete-status .text-button { margin-left:auto; }
.upload-box { display:grid; grid-template-columns:160px minmax(160px,.7fr) minmax(240px,1.3fr) auto; gap:10px; align-items:end; padding:16px; border:1px dashed rgba(27,127,108,.4); border-radius:13px; background:#f7fbfa; }
.upload-box label { display:grid; gap:6px; }
.upload-box label>span { color:var(--muted); font-size:11px; font-weight:700; }
.file-picker { position:relative; min-width:0; }
.file-picker input { position:absolute; width:1px; height:1px; opacity:0; }
.file-picker b { overflow:hidden; padding:11px 12px; border:1px solid var(--border); border-radius:9px; background:#fff; white-space:nowrap; text-overflow:ellipsis; cursor:pointer; font-size:13px; }
.document-list { display:grid; gap:8px; }
.document-row { display:flex; align-items:center; gap:12px; padding:12px; border:1px solid var(--border); border-radius:11px; }
.file-icon { display:grid; place-items:center; flex:none; width:38px; height:38px; border-radius:10px; color:#fff; background:var(--accent); font-weight:800; }
.file-info { display:grid; gap:2px; flex:1; min-width:0; }
.file-info strong,.file-info span { overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.file-info span,.file-info small { color:var(--muted); font-size:12px; }
.document-actions { display:flex; align-items:center; gap:7px; }
.photo-gallery { display:grid; gap:12px; padding-top:18px; border-top:1px solid var(--border); }
.photo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; }
.photo-card { display:grid; gap:6px; min-width:0; }
.photo-card button { overflow:hidden; aspect-ratio:4/3; width:100%; border:1px solid var(--border); border-radius:11px; background:#f4f6f5; cursor:pointer; }
.photo-card img { width:100%; height:100%; object-fit:cover; display:block; }
.photo-card button span { display:grid; place-items:center; height:100%; color:var(--muted); font-weight:800; }
.photo-card strong,.photo-card small { overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.photo-card strong { font-size:13px; }
.photo-card small { color:var(--muted); font-size:12px; }
.mail-hub { display:grid; grid-template-columns:minmax(0,1fr) minmax(280px,.75fr); gap:16px; padding-top:18px; border-top:1px solid var(--border); }
.compact-heading { margin-bottom:14px; }
.mail-list { display:grid; gap:8px; align-content:start; max-height:430px; overflow:auto; }
.mail-row { display:flex; justify-content:space-between; gap:12px; align-items:flex-start; padding:12px; border:1px solid var(--border); border-radius:11px; background:#fbfcfb; }
.mail-row>div { display:grid; gap:3px; min-width:0; }
.mail-row strong,.mail-row span { overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.mail-row span,.mail-row small { color:var(--muted); font-size:12px; }
.mail-error { color:#9d3025 !important; white-space:normal !important; }
@media (max-width:900px) {
  .split-layout,.journal-layout { grid-template-columns:1fr; }
  .sale-layout,.payment-layout { grid-template-columns:1fr; }
  .mail-hub { grid-template-columns:1fr; }
  .erp-hero { align-items:flex-start; flex-direction:column; }
  .upload-box { grid-template-columns:1fr 1fr; }
  .sale-preview { grid-template-columns:1fr; }
}
@media (max-width:600px) {
  .erp-hero,.panel { padding:18px; }
  .erp-form,.journal-basics { grid-template-columns:1fr; }
  .wide { grid-column:auto; }
  .estimate-link { margin-left:0; }
  .upload-box { grid-template-columns:1fr; }
  .document-row { align-items:flex-start; }
  .document-actions { flex-direction:column; }
}
</style>
